import { Component, useState, useEffect, useCallback, useRef } from "react";

// ─── ECharts (bundled / self-hosted — no CDN) ────────────────────────────────
import * as echarts from "echarts/core";
import { BarChart as EBarChart, LineChart as ELineChart, PieChart as EPieChart } from "echarts/charts";
import { GridComponent as EGridComponent, TooltipComponent as ETooltipComponent, LegendComponent as ELegendComponent, MarkLineComponent as EMarkLineComponent, DataZoomComponent as EDataZoomComponent } from "echarts/components";
import { CanvasRenderer as ECanvasRenderer } from "echarts/renderers";
echarts.use([EBarChart, ELineChart, EPieChart, EGridComponent, ETooltipComponent, ELegendComponent, EMarkLineComponent, EDataZoomComponent, ECanvasRenderer]);

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0d1117", color: "#e6edf3" }}>
          <div style={{ textAlign: "center", padding: 32 }}>
            <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Something went wrong</p>
            <p style={{ fontSize: 13, color: "#8b949e", marginBottom: 24 }}>Please refresh the page. If the problem persists, contact support.</p>
            <button onClick={() => window.location.reload()} style={{ background: "#c47a10", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600, padding: "12px 24px" }}>
              Refresh page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Config ────────────────────────────────────────────────────────────────────
// These values come from your .env.local file in the Next.js backend
//
// API_BASE / ENTRA_REDIRECT_URI are derived from the current origin so ONE build
// serves both production (portal.powervisionbi.com) and staging
// (staging.portal.powervisionbi.com) — the app always talks to the API on the same
// host it was loaded from. Falls back to production for non-browser/SSR contexts.
// NOTE: every origin used here must be registered as a redirect URI on the Entra
// app (ENTRA_CLIENT_ID) or Microsoft sign-in will be rejected on that host.
const APP_ORIGIN = (typeof window !== "undefined" && window.location?.origin) || "https://portal.powervisionbi.com";
const API_BASE = `${APP_ORIGIN}/api`;
const ENTRA_CLIENT_ID = "db805003-64da-4cc7-80eb-8e852d8e651f";
const ENTRA_AUTHORITY = "https://login.microsoftonline.com/common";
const ENTRA_REDIRECT_URI = APP_ORIGIN;
// Google Workspace sign-in — ADDITIVE; the Microsoft/Entra path above is unchanged.
// Paste the Google OAuth Web client ID here once created; empty string hides the button.
const GOOGLE_CLIENT_ID = "266786737934-fr41qlqo7ro0frifptmisinbd516n854.apps.googleusercontent.com";
function decodeJwtPayload(t) { try { return JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))); } catch { return {}; } }
// ─── MSAL (Microsoft Authentication Library) ──────────────────────────────────
// Load MSAL from CDN — add this to your index.html:
// <script src="https://alcdn.msauth.net/browser/2.38.0/js/msal-browser.min.js"></script>
let _msalInstance = null;
function getMsalInstance() {
  if (!window.msal) return null;
  if (_msalInstance) return _msalInstance;
  _msalInstance = new window.msal.PublicClientApplication({
    auth: {
      clientId: ENTRA_CLIENT_ID,
      authority: ENTRA_AUTHORITY,
      redirectUri: ENTRA_REDIRECT_URI,
    },
    cache: { cacheLocation: "sessionStorage" },
  });
  return _msalInstance;
}

// ─── Auth helpers ──────────────────────────────────────────────────────────────
async function signInWithEntra() {
  const msal = getMsalInstance();
  if (!msal) throw new Error("MSAL not loaded");
  await msal.initialize();
  await msal.loginRedirect({
    scopes: ["openid", "profile", "email"],
  });
}

async function getAccessToken() {
  const msal = getMsalInstance();
  if (!msal) return null;
  await msal.initialize();
  const accounts = msal.getAllAccounts();
  if (!accounts.length) return null;
  try {
    const result = await msal.acquireTokenSilent({
      scopes: ["openid", "profile", "email"],
      account: accounts[0],
    });
    // Send the ID token (audienced to our app, verifiable) — NOT the access
    // token (audienced to Microsoft Graph, which our API cannot verify).
    return result.idToken;
  } catch {
    return null;
  }
}

async function signOut() {
  const msal = getMsalInstance();
  if (!msal) return;
  await msal.initialize();
  await msal.logoutPopup();
}

// ─── API calls ─────────────────────────────────────────────────────────────────
function downloadExcel(data, question) {
  if (!data?.length) return;
  const headers = Object.keys(data[0]);
  const escape = v => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = '﻿' + [headers.join(','), ...data.map(row => headers.map(h => escape(row[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(question || 'export').slice(0, 40).replace(/[^a-z0-9]/gi, '_')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function extractKpiValue(answer) {
  if (!answer) return null;
  // Strip year-like numbers (1900-2199) so they don't shadow business values
  const cleaned = answer.replace(/\b(19|20|21)\d{2}\b/g, '');
  const m =
    cleaned.match(/[R$£€]\s*[\d,]+(\.\d+)?\s*[KMBkmb]?\b/) ||
    cleaned.match(/\b[\d,]+(\.\d+)?\s*[KMBkmb]\b/) ||
    cleaned.match(/\b[\d]{1,3}(,\d{3})+(\.\d+)?\b/) ||
    cleaned.match(/\b\d+\.\d+%?\b/) ||
    cleaned.match(/\b\d+%\b/) ||
    cleaned.match(/\b\d{4,}\b/) ||
    cleaned.match(/\b\d{1,3}\b/);
  return m ? m[0].trim() : null;
}

function authHeaders(token, impersonateTenantId) {
  const t = token || sessionStorage.getItem("pvbi_token");
  const h = { Authorization: `Bearer ${t}` };
  if (impersonateTenantId) h["X-Impersonate-Tenant"] = impersonateTenantId;
  return h;
}

async function fetchReports(token, impersonateTenantId) {
  const res = await fetch(`${API_BASE}/reports`, {
    headers: authHeaders(token, impersonateTenantId),
  });
  if (!res.ok) throw new Error("Failed to load reports");
  return res.json();
}

async function fetchEmbedToken(token, reportId, impersonateTenantId) {
  const res = await fetch(`${API_BASE}/embed-token`, {
    method: "POST",
    headers: { ...authHeaders(token, impersonateTenantId), "Content-Type": "application/json" },
    body: JSON.stringify({ reportId }),
  });
  if (!res.ok) throw new Error("Failed to get embed token");
  return res.json();
}

// ─── Theme ─────────────────────────────────────────────────────────────────────
const T = {
  bg: "#0d1117", surface: "#161b22", surfaceHover: "#1f2937",
  border: "#21262d", borderHover: "#30363d",
  accent: "#c47a10", accentDark: "#a86209", accentLight: "#e8950f",
  accentGlow: "rgba(196,122,16,0.15)", accentGlow2: "rgba(196,122,16,0.07)",
  success: "#2ea043", successGlow: "rgba(46,160,67,0.15)",
  error: "#f85149", errorGlow: "rgba(248,81,73,0.12)",
  warn: "#d29922", textPrimary: "#e6edf3",
  textSecondary: "#8b949e", textMuted: "#484f58",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Plus Jakarta Sans',sans-serif;background:${T.bg};color:${T.textPrimary};min-height:100vh;overflow-x:hidden}
  .mono{font-family:'JetBrains Mono',monospace}
  .grid-bg{position:fixed;inset:0;pointer-events:none;z-index:0;
    background-image:linear-gradient(rgba(196,122,16,0.035) 1px,transparent 1px),linear-gradient(90deg,rgba(196,122,16,0.035) 1px,transparent 1px);
    background-size:48px 48px}
  .orb{position:fixed;border-radius:50%;pointer-events:none;z-index:0;filter:blur(90px)}
  @keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
  @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  @keyframes pulse2{0%,100%{opacity:1}50%{opacity:.45}}
  .fu{animation:fadeUp .45s ease both}
  .fu1{animation:fadeUp .45s .06s ease both}
  .fu2{animation:fadeUp .45s .13s ease both}
  .fu3{animation:fadeUp .45s .2s ease both}
  .spin{animation:spin .7s linear infinite}
  .field-group{display:flex;flex-direction:column;gap:6px}
  .field-label{font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:${T.textSecondary}}
  .field-input{background:rgba(255,255,255,.04);border:1px solid ${T.border};border-radius:8px;color:${T.textPrimary};font-family:'Plus Jakarta Sans',sans-serif;font-size:14px;padding:11px 14px;outline:none;transition:border-color .2s,box-shadow .2s,background .2s;width:100%}
  .field-input:focus{border-color:${T.accent};box-shadow:0 0 0 3px ${T.accentGlow};background:rgba(196,122,16,.04)}
  .field-input::placeholder{color:${T.textMuted}}
  .btn-primary{background:${T.accent};border:none;border-radius:8px;color:#fff;cursor:pointer;font-family:'Plus Jakarta Sans',sans-serif;font-size:14px;font-weight:600;padding:12px 24px;transition:background .2s,transform .1s,box-shadow .2s;width:100%}
  .btn-primary:hover{background:${T.accentLight};box-shadow:0 0 28px ${T.accentGlow};transform:translateY(-1px)}
  .btn-primary:active{transform:translateY(0)}
  .btn-primary:disabled{opacity:.45;cursor:not-allowed;transform:none}
  .btn-secondary{background:transparent;border:1px solid ${T.border};border-radius:8px;color:${T.textSecondary};cursor:pointer;font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;font-weight:500;padding:9px 18px;transition:border-color .2s,color .2s,background .2s}
  .btn-secondary:hover{border-color:${T.borderHover};color:${T.textPrimary};background:rgba(255,255,255,.04)}
  .btn-ghost{background:none;border:none;color:${T.accent};cursor:pointer;font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;font-weight:500;padding:4px 0;transition:opacity .2s}
  .btn-ghost:hover{opacity:.72}
  .card{background:${T.surface};border:1px solid ${T.border};border-radius:12px;padding:24px}
  .divider{border:none;border-top:1px solid ${T.border};margin:20px 0}
  .spinner{width:17px;height:17px;border:2px solid rgba(255,255,255,.2);border-top-color:#fff;border-radius:50%;display:inline-block}
  .alert{border-radius:8px;padding:12px 16px;font-size:13px;font-weight:500;display:flex;align-items:flex-start;gap:10px;animation:fadeUp .3s ease}
  .alert-error{background:${T.errorGlow};border:1px solid rgba(248,81,73,.25);color:#fca5a5}
  .alert-success{background:${T.successGlow};border:1px solid rgba(46,160,67,.25);color:#86efac}
  .alert-info{background:${T.accentGlow2};border:1px solid rgba(196,122,16,.2);color:#fcd34d}
  .badge{display:inline-flex;align-items:center;gap:5px;border-radius:20px;font-size:11px;font-weight:600;letter-spacing:.04em;padding:3px 10px}
  .badge-success{background:${T.successGlow};color:${T.success};border:1px solid rgba(46,160,67,.25)}
  .badge-info{background:${T.accentGlow};color:${T.accent};border:1px solid rgba(196,122,16,.25)}
  .nav-item{display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:500;transition:background .15s,color .15s;color:${T.textSecondary};border:none;background:none;width:100%;text-align:left;font-family:'Plus Jakarta Sans',sans-serif}
  .nav-item:hover{background:rgba(255,255,255,.05);color:${T.textPrimary}}
  .nav-item.active{background:${T.accentGlow};color:${T.accent}}
  .report-card{background:${T.surface};border:1px solid ${T.border};border-radius:10px;padding:20px;cursor:pointer;transition:border-color .2s,transform .2s,box-shadow .2s;animation:fadeUp .4s ease both}
  .report-card:hover{border-color:${T.accent};transform:translateY(-2px);box-shadow:0 8px 32px rgba(196,122,16,.12)}
  .stat-card{background:${T.surface};border:1px solid ${T.border};border-radius:10px;padding:18px 20px;animation:fadeUp .4s ease both}
  ::-webkit-scrollbar{width:5px}
  ::-webkit-scrollbar-track{background:transparent}
  ::-webkit-scrollbar-thumb{background:${T.border};border-radius:3px}
  @media print{
    body > * { display: none !important; }
    #report-print-frame { display: block !important; position: fixed; inset: 0; width: 100vw; height: 100vh; border: none; z-index: 9999; background: white; }
  }
  .hamburger-btn{display:none;align-items:center;justify-content:center;background:none;border:1px solid ${T.border};border-radius:8px;color:${T.textSecondary};cursor:pointer;padding:8px;flex-shrink:0}
  .sidebar-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:199;cursor:pointer}
  @media(max-width:768px){
    .hamburger-btn{display:flex !important}
    .mob-sidebar{position:fixed !important;left:0;top:0;bottom:0;z-index:200;transform:translateX(-100%);transition:transform .3s ease;overflow-y:auto}
    .mob-sidebar.open{transform:translateX(0) !important;box-shadow:4px 0 32px rgba(0,0,0,.6)}
    .sidebar-overlay.open{display:block}
    .kpi-grid{grid-template-columns:1fr !important}
    .reports-grid{grid-template-columns:1fr !important}
    .mob-pad{padding:18px 14px !important}
    .mob-stats{grid-template-columns:1fr !important}
    .mob-header{flex-direction:column;align-items:flex-start !important;gap:12px}
  }
  @media(min-width:769px) and (max-width:1100px){
    .kpi-grid{grid-template-columns:repeat(2,1fr) !important}
    .reports-grid{grid-template-columns:repeat(2,1fr) !important}
  }
`;

const Icon = ({ n, size = 16, color = "currentColor" }) => {
  const icons = {
    eye: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
    db: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
    chart: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    grid: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
    settings: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
    logout: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
    check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    x: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    alert: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    user: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    lock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
    refresh: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
    external: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
    microsoft: <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><rect x="1" y="1" width="10.5" height="10.5" fill="#f25022"/><rect x="12.5" y="1" width="10.5" height="10.5" fill="#7fba00"/><rect x="1" y="12.5" width="10.5" height="10.5" fill="#00a4ef"/><rect x="12.5" y="12.5" width="10.5" height="10.5" fill="#ffb900"/></svg>,
    zap: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    home: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
    send: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
    bell: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
    trash: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>,
    star: <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    starEmpty: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    messageSquare: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    shield: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    menu: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  };
  return icons[n] || null;
};

const CAT_COLOR = { Finance: T.accent, Sales: T.success, Operations: "#a78bfa", Purchasing: T.warn };

// ─── Chart components ──────────────────────────────────────────────────────────
const CHART_COLORS = ["#c47a10", "#2ea043", "#3b82f6", "#a78bfa", "#f59e0b", "#ef4444", "#06b6d4", "#ec4899"];

// (Legacy hand-rolled SVG BarChart/LineChart/PieChart removed — all charts now
//  render through the premium ECharts ChartWidget below.)

// Premium ECharts renderer for AI-assistant chart results (bar / line / pie).
// Used by every client's chat, so this is the shared "all reports" chart surface.
function ChartWidget({ chart }) {
  if (!chart || !chart.data?.length) return null;
  const TH = chartTheme();
  const names = chart.data.map(d => d.name);
  const values = chart.data.map(d => Number(d.value) || 0);
  const fmtNum = v => { const n = Number(v) || 0; return n >= 1e6 ? `${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `${(n/1e3).toFixed(1)}K` : `${n}`; };

  let option;
  if (chart.type === "pie") {
    option = {
      animationDuration: 600,
      tooltip: { ...TH.tooltip, trigger: "item", formatter: p => `${p.name}: ${fmtNum(p.value)} (${p.percent}%)` },
      legend: { type: "scroll", orient: "vertical", right: 6, top: "center", textStyle: { color: T.textSecondary, fontSize: 11 }, icon: "roundRect", itemWidth: 12, itemHeight: 8 },
      series: [{
        type: "pie", radius: ["42%", "70%"], center: ["36%", "52%"], avoidLabelOverlap: true, padAngle: 2,
        itemStyle: { borderRadius: 6, borderColor: T.bg, borderWidth: 2 },
        data: chart.data.map((d, i) => ({ name: d.name, value: Number(d.value) || 0, itemStyle: { color: CHART_COLORS[i % CHART_COLORS.length] } })),
        label: { color: T.textSecondary, fontSize: 11 }, labelLine: { lineStyle: { color: T.border } },
      }],
    };
  } else if (chart.type === "line") {
    option = {
      animationDuration: 700,
      grid: { top: 24, right: 18, bottom: names.length > 6 ? 56 : 38, left: 56 },
      tooltip: { ...TH.tooltip, valueFormatter: v => fmtNum(v) },
      xAxis: { type: "category", data: names, boundaryGap: false, axisLabel: { ...TH.axisLabel, interval: 0, rotate: names.length > 6 ? 30 : 0 }, axisLine: TH.axisLine, axisTick: { show: false } },
      yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: fmtNum }, splitLine: TH.splitLine, axisLine: { show: false } },
      series: [{ type: "line", smooth: true, data: values, lineStyle: { color: T.accent, width: 3 }, itemStyle: { color: T.accent }, symbol: "circle", symbolSize: 7, areaStyle: { color: TH.grad("rgba(196,122,16,0.28)", "rgba(196,122,16,0.02)") } }],
    };
  } else {
    option = {
      animationDuration: 700,
      grid: { top: 24, right: 18, bottom: names.length > 5 ? 60 : 38, left: 56 },
      tooltip: { ...TH.tooltip, valueFormatter: v => fmtNum(v) },
      xAxis: { type: "category", data: names, axisLabel: { ...TH.axisLabel, interval: 0, rotate: names.length > 5 ? 30 : 0 }, axisLine: TH.axisLine, axisTick: { show: false } },
      yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: fmtNum }, splitLine: TH.splitLine, axisLine: { show: false } },
      series: [{ type: "bar", barWidth: "55%", data: values.map((v, i) => ({ value: v, itemStyle: { color: TH.grad(CHART_COLORS[i % CHART_COLORS.length], CHART_COLORS[i % CHART_COLORS.length] + "88"), borderRadius: [5, 5, 0, 0] } })), label: { show: values.length <= 12, position: "top", formatter: p => fmtNum(p.value), color: T.textMuted, fontSize: 10 } }],
    };
  }

  return (
    <div style={{ marginTop: 12, padding: "14px 12px", background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
      {chart.title && <p style={{ fontSize: 12, fontWeight: 600, color: T.textPrimary, textAlign: "center", marginBottom: 8 }}>{chart.title}</p>}
      <EChart option={option} height={300} />
    </div>
  );
}

// ─── Login ─────────────────────────────────────────────────────────────────────
// Now uses Microsoft Entra External ID via MSAL popup
function Login({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const googleDivRef = useRef(null);

  const handleMicrosoftSignIn = async () => {
    setErr(""); setLoading(true);
    try {
      await signInWithEntra(); // redirects away — no return value
    } catch (e) {
      setErr("Sign-in failed. Please try again or contact Power Vision BI support.");
      setLoading(false);
    }
  };

  // Google Workspace sign-in via Google Identity Services (renders Google's button +
  // One Tap auto sign-in for returning users). Token is verified server-side by issuer.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let tries = 0;
    const iv = setInterval(() => {
      const gid = window.google?.accounts?.id;
      if (gid) {
        clearInterval(iv);
        gid.initialize({
          client_id: GOOGLE_CLIENT_ID,
          auto_select: true,
          callback: (resp) => {
            const t = resp.credential;
            const p = decodeJwtPayload(t);
            sessionStorage.setItem("pvbi_provider", "google");
            onLogin({ email: p.email, name: p.name || (p.email || "").split("@")[0] }, t);
          },
        });
        if (googleDivRef.current) {
          gid.renderButton(googleDivRef.current, { theme: "outline", size: "large", width: 336, text: "continue_with", shape: "rectangular" });
        }
        gid.prompt(); // One Tap for returning users
      } else if (++tries > 40) {
        clearInterval(iv); // GIS script didn't load (~10s) — Microsoft sign-in still works
      }
    }, 250);
    return () => clearInterval(iv);
  }, [onLogin]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, position: "relative", zIndex: 1 }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div className="fu" style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 40 }}>
          <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: T.textPrimary }}>
            Power<span style={{ color: T.accent }}>Vision</span> BI
          </div>
          <div style={{ marginTop: 10, height: 1, width: 200, background: `linear-gradient(90deg, transparent, ${T.accent}, transparent)` }} />
          <p style={{ marginTop: 10, fontSize: 12, color: T.textMuted, letterSpacing: "0.06em" }}>CLIENT ANALYTICS PORTAL</p>
        </div>

        <div className="card fu1" style={{ padding: 32 }}>
          <h2 style={{ fontSize: 19, fontWeight: 600, marginBottom: 4 }}>Welcome back</h2>
          <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 28 }}>Sign in with your company account to view your reports</p>

          {err && <div className="alert alert-error" style={{ marginBottom: 18 }}><Icon n="alert" size={15} color={T.error} />{err}</div>}

          {/* Microsoft sign-in button */}
          <button
            className="btn-primary"
            onClick={handleMicrosoftSignIn}
            disabled={loading}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: "#fff", color: "#1a1a1a", border: "1px solid #ddd" }}
          >
            {loading
              ? <><span className="spinner spin" style={{ borderTopColor: T.accent, borderColor: "rgba(196,122,16,.3)" }} />Signing in…</>
              : <><Icon n="microsoft" size={18} />Continue with Microsoft</>
            }
          </button>

          {/* Google Workspace sign-in (renders only when configured) */}
          {GOOGLE_CLIENT_ID && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0 12px" }}>
                <span style={{ flex: 1, height: 1, background: T.border }} />
                <span style={{ fontSize: 11, color: T.textMuted }}>or</span>
                <span style={{ flex: 1, height: 1, background: T.border }} />
              </div>
              <div ref={googleDivRef} style={{ display: "flex", justifyContent: "center" }} />
            </div>
          )}

          <hr className="divider" />
          <p style={{ fontSize: 12, color: T.textMuted, textAlign: "center" }}>
            New customer? <button className="btn-ghost" style={{ fontSize: 12 }}>Contact Power Vision BI</button>
          </p>
        </div>

        <p className="fu2" style={{ textAlign: "center", marginTop: 24, fontSize: 11, color: T.textMuted }}>
          <span style={{ color: "#ffffff", fontWeight: 600, letterSpacing: "0.04em" }}>We Make Data Dance!</span> · Secured with Microsoft Entra ID
        </p>
      </div>
    </div>
  );
}

// ─── Dashboard ──────────────────────────────────────────────────────────────────
// Now loads real reports from /api/reports and embeds via /api/embed-token
// ─── ECharts wrapper (premium, themed, responsive) ───────────────────────────
function EChart({ option, height = 320 }) {
  const ref = useRef(null);
  const inst = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    if (!inst.current) inst.current = echarts.init(ref.current, null, { renderer: "canvas" });
    inst.current.setOption(option, true);
    const ro = new ResizeObserver(() => { if (inst.current) inst.current.resize(); });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [option]);
  useEffect(() => () => { if (inst.current) { inst.current.dispose(); inst.current = null; } }, []);
  return <div ref={ref} style={{ width: "100%", height }} />;
}

// Shared premium chart styling, parameterised by the portal theme tokens (T).
function chartTheme() {
  const grad = (c1, c2) => new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: c1 }, { offset: 1, color: c2 },
  ]);
  return {
    grad,
    axisLabel: { color: T.textMuted, fontSize: 10, fontFamily: "'Plus Jakarta Sans',sans-serif" },
    axisLine: { lineStyle: { color: T.border } },
    splitLine: { lineStyle: { color: T.border, type: "dashed", opacity: 0.4 } },
    tooltip: {
      trigger: "axis",
      backgroundColor: T.surface,
      borderColor: T.border,
      borderWidth: 1,
      padding: [8, 12],
      textStyle: { color: T.textPrimary, fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 12 },
      axisPointer: { type: "shadow", shadowStyle: { color: "rgba(196,122,16,0.07)" } },
      extraCssText: "box-shadow:0 8px 24px rgba(0,0,0,0.35);border-radius:8px;",
    },
  };
}

function AnalyticsCharts({ data }) {
  const COLORS = ["#c47a10","#2ea043","#3b82f6","#a78bfa","#f59e0b","#ef4444","#06b6d4","#ec4899","#84cc16","#f97316","#8b5cf6","#14b8a6"];
  const safe = v => (v == null || isNaN(v)) ? 0 : Number(v);
  const fmt = v => { const n = safe(v); return n >= 1e6 ? `$${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n/1e3).toFixed(0)}K` : `$${n.toFixed(0)}`; };
  const money = v => `$${safe(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const cardStyle = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "20px 20px 12px" };

  // ── Premium ECharts options (bundled, self-hosted) ──────────────────────────
  const TH = chartTheme();
  const fmt0 = v => { const n = safe(v); return n >= 1e6 ? `$${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n/1e3).toFixed(0)}K` : `$${Math.round(n)}`; };

  const monthlyRevOption = {
    animationDuration: 700, animationEasing: "cubicOut",
    grid: { top: 28, right: 18, bottom: 36, left: 64 },
    tooltip: { ...TH.tooltip, valueFormatter: v => money(v) },
    xAxis: { type: "category", data: data.monthlyRevenue.labels, axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: fmt0 }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [{
      type: "bar", data: data.monthlyRevenue.revenue.map(safe), barWidth: "55%",
      itemStyle: { borderRadius: [6, 6, 0, 0], color: TH.grad("#e0a836", T.accent) },
      emphasis: { itemStyle: { color: TH.grad("#f2bb4d", "#d4870f") } },
      label: { show: true, position: "top", formatter: p => fmt0(p.value), color: T.textMuted, fontSize: 10, fontFamily: "'Plus Jakarta Sans',sans-serif" },
      animationDelay: i => i * 40,
    }],
  };

  const gpOption = {
    animationDuration: 800,
    grid: { top: 28, right: 18, bottom: 36, left: 48 },
    tooltip: { ...TH.tooltip, valueFormatter: v => v == null ? "—" : `${Number(v).toFixed(1)}%` },
    xAxis: { type: "category", data: data.monthlyRevenue.labels, boundaryGap: false, axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", min: 0, max: 100, axisLabel: { ...TH.axisLabel, formatter: "{value}%" }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [{
      type: "line", smooth: true, connectNulls: true,
      data: data.monthlyRevenue.gp.map(v => v == null || isNaN(v) ? null : Number(v)),
      lineStyle: { color: "#a78bfa", width: 3 }, itemStyle: { color: "#a78bfa" },
      symbol: "circle", symbolSize: 7,
      areaStyle: { color: TH.grad("rgba(167,139,250,0.35)", "rgba(167,139,250,0.02)") },
      label: { show: true, position: "top", formatter: p => p.value == null ? "" : `${p.value}%`, color: T.textMuted, fontSize: 10 },
    }],
  };

  const topCustNames = [...data.topCustomers.names].reverse();
  const topCustRev = [...data.topCustomers.revenue].reverse();
  const topCustOption = {
    animationDuration: 700,
    grid: { top: 12, right: 64, bottom: 28, left: 132 },
    tooltip: { ...TH.tooltip, trigger: "item", valueFormatter: v => money(v) },
    xAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: fmt0 }, splitLine: TH.splitLine, axisLine: { show: false } },
    yAxis: { type: "category", data: topCustNames, axisLabel: { ...TH.axisLabel, fontSize: 11 }, axisLine: TH.axisLine, axisTick: { show: false } },
    series: [{
      type: "bar", barWidth: "62%",
      data: topCustRev.map((v, i) => ({ value: safe(v), itemStyle: { color: COLORS[(topCustNames.length - 1 - i) % COLORS.length], borderRadius: [0, 5, 5, 0] } })),
      label: { show: true, position: "right", formatter: p => fmt0(p.value), color: T.textMuted, fontSize: 10 },
    }],
  };

  const salesOption = {
    animationDuration: 700,
    grid: { top: 28, right: 18, bottom: data.salesperson.names.length > 6 ? 56 : 38, left: 64 },
    tooltip: { ...TH.tooltip, valueFormatter: v => money(v) },
    xAxis: { type: "category", data: data.salesperson.names, axisLabel: { ...TH.axisLabel, interval: 0, rotate: data.salesperson.names.length > 6 ? 30 : 0 }, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: fmt0 }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [{
      type: "bar", barWidth: "55%",
      data: data.salesperson.revenue.map((v, i) => ({ value: safe(v), itemStyle: { color: TH.grad(COLORS[i % COLORS.length], COLORS[i % COLORS.length] + "88"), borderRadius: [5, 5, 0, 0] } })),
      label: { show: true, position: "top", formatter: p => fmt0(p.value), color: T.textMuted, fontSize: 10 },
    }],
  };

  const yoyColors = [T.accent, "#2ea043", "#3b82f6"];
  const yoyOption = {
    animationDuration: 800,
    grid: { top: 32, right: 20, bottom: 34, left: 68 },
    tooltip: { ...TH.tooltip, valueFormatter: v => v == null ? "—" : money(v) },
    legend: { data: data.yoy.series.map(s => String(s.year)), top: 2, right: 4, textStyle: { color: T.textSecondary, fontSize: 11 }, icon: "roundRect", itemWidth: 12, itemHeight: 8 },
    xAxis: { type: "category", data: data.yoy.months, boundaryGap: false, axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: fmt0 }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: data.yoy.series.map((s, i) => ({
      name: String(s.year), type: "line", smooth: true, connectNulls: true,
      data: s.values.map(v => v == null || isNaN(v) ? null : Number(v)),
      lineStyle: { color: yoyColors[i % yoyColors.length], width: 2.5 },
      itemStyle: { color: yoyColors[i % yoyColors.length] }, symbol: "circle", symbolSize: 5,
      ...(i === 0 ? { areaStyle: { color: TH.grad("rgba(196,122,16,0.18)", "rgba(196,122,16,0.01)") } } : {}),
    })),
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

      {/* Row 1: Monthly Revenue + GP% */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={cardStyle}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Monthly Revenue <span style={{ color: T.textMuted, fontWeight: 400 }}>— {new Date().getFullYear()}</span></p>
          <EChart option={monthlyRevOption} height={300} />
        </div>
        <div style={cardStyle}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Gross Profit % <span style={{ color: T.textMuted, fontWeight: 400 }}>— {new Date().getFullYear()}</span></p>
          <EChart option={gpOption} height={300} />
        </div>
      </div>

      {/* Row 2: Top 10 Customers + Salesperson */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={cardStyle}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Top 10 Customers <span style={{ color: T.textMuted, fontWeight: 400 }}>— YTD</span></p>
          <EChart option={topCustOption} height={320} />
        </div>
        <div style={cardStyle}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Revenue by Salesperson <span style={{ color: T.textMuted, fontWeight: 400 }}>— YTD</span></p>
          <EChart option={salesOption} height={320} />
        </div>
      </div>

      {/* Row 3: Year-over-Year */}
      <div style={cardStyle}>
        <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Year-over-Year Monthly Revenue</p>
        <EChart option={yoyOption} height={280} />
      </div>

      {/* Row 4: Commission Payout Table */}
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>
            Commission Payout — Paid Invoices <span style={{ color: T.textMuted, fontWeight: 400 }}>({data.commissions.length} records)</span>
          </p>
          <span style={{ fontSize: 12, color: T.textMuted }}>
            Total comm: <strong style={{ color: T.accent }}>{money(data.commissions.reduce((s, r) => s + safe(r.commAmt), 0))}</strong>
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                {["Invoice","Date","Customer","Salesperson","Sales Order","Invoice Amt","SO Value","% of SO","Comm %","Commission","Payment Date"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: h === "Invoice" || h === "Date" || h === "Customer" || h === "Salesperson" || h === "Sales Order" ? "left" : "right", fontSize: 10, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.commissions.map((r, i) => {
                const pct = r.pctOfOrder;
                const pctColor = pct == null ? T.textMuted : pct >= 90 ? T.success : pct >= 50 ? T.warn : T.error;
                return (
                <tr key={i} style={{ borderBottom: `1px solid ${T.border}`, background: i % 2 === 0 ? "rgba(255,255,255,0.01)" : "transparent" }}>
                  <td style={{ padding: "7px 12px", color: T.textSecondary, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{r.invoiceNo}</td>
                  <td style={{ padding: "7px 12px", color: T.textMuted, whiteSpace: "nowrap" }}>{r.date}</td>
                  <td style={{ padding: "7px 12px", color: T.textPrimary, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.customer}</td>
                  <td style={{ padding: "7px 12px", color: T.textSecondary }}>{r.salesperson}</td>
                  <td style={{ padding: "7px 12px", color: T.textSecondary, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{r.salesOrderNo}</td>
                  <td style={{ padding: "7px 12px", color: T.textPrimary, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{money(r.amount)}</td>
                  <td style={{ padding: "7px 12px", color: T.textSecondary, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{r.orderAmount > 0 ? money(r.orderAmount) : "—"}</td>
                  <td style={{ padding: "7px 12px", textAlign: "right", fontWeight: 700, color: pctColor, whiteSpace: "nowrap" }}>{pct != null ? `${pct}%` : "—"}</td>
                  <td style={{ padding: "7px 12px", color: T.textMuted, textAlign: "right" }}>{safe(r.commRate)}%</td>
                  <td style={{ padding: "7px 12px", color: T.accent, textAlign: "right", fontWeight: 600, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{money(r.commAmt)}</td>
                  <td style={{ padding: "7px 12px", color: T.success, whiteSpace: "nowrap" }}>{r.paymentDate}</td>
                </tr>
                );
              })}
              {data.commissions.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 24, textAlign: "center", color: T.textMuted }}>No paid commission records found for this year</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Sales Order Detail component ────────────────────────────────────────────
function SalesOrderDetail({ data }) {
  const { header, summary, lineItems, invoices, invoiceLines, videos, documents, listDocs } = data;
  const [activeVideo, setActiveVideo] = useState(null);
  const fmt = v => `$${(v||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US') : '—';
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "18px 20px" };
  const th = { padding: "8px 12px", textAlign: "left", fontSize: 10, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" };
  const td = { padding: "7px 12px", fontSize: 12, borderBottom: `1px solid ${T.border}` };

  // ── Charts (ECharts, bundled — no CDN) ─────────────────────────────────────
  const TH = chartTheme();
  const numv = v => Number(v) || 0;
  const fmtK = v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${Math.round(v)}`;

  const qtyLines = lineItems.filter(l => numv(l.qtyOrdered) > 0).slice(0, 15);
  const qtyOption = {
    animationDuration: 600,
    legend: { data: ["Ordered", "Shipped", "Backorder"], top: 0, textStyle: { color: T.textSecondary, fontSize: 11 }, icon: "roundRect", itemWidth: 12, itemHeight: 8 },
    grid: { top: 32, right: 12, bottom: 72, left: 48 },
    tooltip: { ...TH.tooltip },
    xAxis: { type: "category", data: qtyLines.map(l => (l.itemCode || "").slice(0, 14)), axisLabel: { ...TH.axisLabel, interval: 0, rotate: 35 }, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", axisLabel: TH.axisLabel, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [
      { name: "Ordered", type: "bar", data: qtyLines.map(l => numv(l.qtyOrdered)), itemStyle: { color: "rgba(196,122,16,0.85)", borderRadius: [3, 3, 0, 0] } },
      { name: "Shipped", type: "bar", data: qtyLines.map(l => numv(l.qtyShipped)), itemStyle: { color: "rgba(46,160,67,0.9)", borderRadius: [3, 3, 0, 0] } },
      { name: "Backorder", type: "bar", data: qtyLines.map(l => numv(l.qtyBackordered)), itemStyle: { color: "rgba(248,81,73,0.85)", borderRadius: [3, 3, 0, 0] } },
    ],
  };

  const invVals = invoices.map(i => numv(i.value));
  const orderVal = numv(summary.totalOrdered);
  const invYMax = Math.max(...invVals, orderVal, 0) * 1.2 || 1000;
  const invOption = {
    animationDuration: 600,
    grid: { top: 24, right: 16, bottom: 40, left: 64 },
    tooltip: { ...TH.tooltip, valueFormatter: v => `$${numv(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}` },
    xAxis: { type: "category", data: invoices.map(i => i.invoiceNo), axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", max: invYMax, axisLabel: { ...TH.axisLabel, formatter: fmtK }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [{
      name: "Invoice Value", type: "bar", barWidth: "55%", data: invVals,
      itemStyle: { color: TH.grad("rgba(46,160,67,0.95)", "rgba(46,160,67,0.5)"), borderRadius: [5, 5, 0, 0] },
      label: { show: true, position: "top", formatter: p => p.value > 0 ? `$${(p.value / 1000).toFixed(1)}K` : "", color: T.textMuted, fontSize: 10 },
      markLine: {
        symbol: "none", silent: true,
        lineStyle: { color: T.accent, type: "dashed", width: 1.5 },
        label: { formatter: "Order value", position: "insideEndTop", color: T.accent, fontSize: 10 },
        data: [{ yAxis: orderVal }],
      },
    }],
  };

  const statusColor = header.orderStatus === 'O' ? T.success : header.orderStatus === 'C' ? T.textMuted : T.warn;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Header summary */}
      <div style={{ ...card, borderLeft: `4px solid ${T.accent}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: T.accent }}>SO {header.salesOrderNo}</h2>
              <span style={{ background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}44`, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                {header.orderStatus === 'O' ? 'Open' : header.orderStatus === 'C' ? 'Completed' : header.orderStatus || 'Unknown'}
              </span>
              {header.orderType && header.orderType !== 'S' && (
                <span style={{ background: "rgba(168,85,247,.15)", color: "#a855f7", border: "1px solid rgba(168,85,247,.3)", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                  {header.orderType === 'Q' ? 'Quote' : header.orderType === 'M' ? 'Master Order' : header.orderType === 'B' ? 'Back Order' : header.orderType === 'R' ? 'Repeating' : header.orderType}
                </span>
              )}
              {data.fromHistory && <span style={{ background: T.accentGlow2, color: T.accent, border: `1px solid ${T.accent}44`, borderRadius: 20, padding: "2px 10px", fontSize: 10 }}>History</span>}
              {data.fromInvoicesOnly && <span style={{ background: "rgba(59,130,246,.15)", color: "#3b82f6", border: "1px solid rgba(59,130,246,.3)", borderRadius: 20, padding: "2px 10px", fontSize: 10 }}>From AR Invoices</span>}
            </div>
            <p style={{ fontSize: 15, fontWeight: 600, color: T.textPrimary, marginBottom: 4 }}>{header.billToName || header.customerName}</p>
            {header.projectName && <p style={{ fontSize: 13, color: T.textSecondary }}>Project: {header.projectName}</p>}
            {header.customerPONo && <p style={{ fontSize: 12, color: T.textMuted }}>Customer PO: {header.customerPONo}</p>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 24px", textAlign: "right" }}>
            {[
              ["Order Date", fmtDate(header.orderDate)],
              ["Salesperson", header.salespersonNo || "—"],
              ["Order Value", fmt(header.orderValue)],
              ["Invoiced", fmt(summary.totalInvoiced)],
              ["GP%", `${summary.gpPct}%`],
              ["Fill Rate", `${summary.fillRate}%`],
            ].map(([label, value]) => (
              <div key={label}>
                <p style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</p>
                <p style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary, fontFamily: label.includes('Date') || label.includes('person') ? undefined : "'JetBrains Mono',monospace" }}>{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
        {[
          { label: "Line Items", value: summary.lineCount, color: T.accent },
          { label: "Invoices", value: summary.invoiceCount, color: T.success },
          { label: "Ordered Qty", value: summary.totalOrderedQty, color: T.warn },
          { label: "Shipped Qty", value: summary.totalShippedQty, color: "#3b82f6" },
        ].map(k => (
          <div key={k.label} style={{ ...card, textAlign: "center" }}>
            <p style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{k.label}</p>
            <p style={{ fontSize: 26, fontWeight: 700, color: k.color, fontFamily: "'JetBrains Mono',monospace" }}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      {lineItems.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={card}>
            <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 12 }}>Qty: Ordered vs Shipped vs Backorder</p>
            <EChart option={qtyOption} height={280} />
          </div>
          <div style={card}>
            <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 12 }}>Invoices vs Order Value</p>
            {invoices.length > 0
              ? <EChart option={invOption} height={280} />
              : <div style={{ height: 280, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.textMuted, fontSize: 13, gap: 6 }}>
                  <span>{header.orderType === 'Q' ? 'Quote — not invoiced' : 'No invoices yet'}</span>
                  {header.orderType === 'Q' && <span style={{ fontSize: 11 }}>Must be converted to a Sales Order before invoicing</span>}
                </div>}
          </div>
        </div>
      )}

      {/* Line Items table */}
      {lineItems.length > 0 && (
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 14 }}>Line Items ({lineItems.length})</p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr>
                {["Item Code","Description","Qty Ordered","Qty Shipped","Backorder","Unit Price","Amount","Promise Date","Comment"].map(h => (
                  <th key={h} style={{ ...th, textAlign: h === "Description" || h === "Item Code" || h === "Comment" ? "left" : "right" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{lineItems.map((l, i) => (
                <tr key={i} style={{ background: i%2===0 ? "rgba(255,255,255,.01)" : "transparent" }}>
                  <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", color: T.accent }}>{l.itemCode}</td>
                  <td style={{ ...td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.description}</td>
                  <td style={{ ...td, textAlign: "right" }}>{l.qtyOrdered}</td>
                  <td style={{ ...td, textAlign: "right", color: l.qtyShipped > 0 ? T.success : T.textMuted }}>{l.qtyShipped}</td>
                  <td style={{ ...td, textAlign: "right", color: l.qtyBackordered > 0 ? T.error : T.textMuted }}>{l.qtyBackordered || "—"}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "'JetBrains Mono',monospace" }}>{fmt(l.unitPrice)}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{fmt(l.extAmount)}</td>
                  <td style={{ ...td, textAlign: "right", color: T.textMuted, whiteSpace: "nowrap" }}>{fmtDate(l.promiseDate)}</td>
                  <td style={{ ...td, color: T.textMuted, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.comment || "—"}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoices table */}
      {invoices.length > 0 && (
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 14 }}>Invoices ({invoices.length}) — Total {fmt(summary.totalInvoiced)}</p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr>
              {["Invoice #","Date","Due Date","Value","Cost","Freight","Commission","Salesperson"].map(h => (
                <th key={h} style={{ ...th, textAlign: h==="Invoice #" || h==="Salesperson" ? "left" : "right" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{invoices.map((inv, i) => (
              <tr key={i} style={{ background: i%2===0 ? "rgba(255,255,255,.01)" : "transparent" }}>
                <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", color: T.success }}>{inv.invoiceNo}</td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", color: T.textMuted }}>{fmtDate(inv.invoiceDate)}</td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", color: T.textMuted }}>{fmtDate(inv.dueDate)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{fmt(inv.value)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", color: T.textMuted }}>{fmt(inv.cost)}</td>
                <td style={{ ...td, textAlign: "right", color: T.textMuted }}>{fmt(inv.freight)}</td>
                <td style={{ ...td, textAlign: "right", color: T.accent }}>{fmt(inv.commission)}</td>
                <td style={{ ...td, color: T.textSecondary }}>{inv.salesperson || "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {/* YouTube Videos — inline player */}
      {videos.length > 0 && (
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 14 }}>
            <Icon n="external" size={14} color="#ef4444" /> YouTube Videos ({videos.length})
          </p>
          {/* Inline player */}
          {activeVideo && (
            <div style={{ marginBottom: 16, borderRadius: 10, overflow: "hidden", background: "#000", position: "relative" }}>
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1`}
                style={{ width: "100%", height: 420, border: "none", display: "block" }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen title="YouTube Video" />
              <button onClick={() => setActiveVideo(null)}
                style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,.7)", border: "none", borderRadius: 6, color: "#fff", cursor: "pointer", padding: "4px 10px", fontSize: 12 }}>✕ Close</button>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            {videos.map((v, i) => (
              <div key={i} onClick={() => setActiveVideo(activeVideo === v.id ? null : v.id)}
                style={{ background: T.bg, border: `2px solid ${activeVideo === v.id ? T.accent : T.border}`, borderRadius: 10, overflow: "hidden", cursor: "pointer", transition: "border-color .2s, transform .15s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = activeVideo === v.id ? T.accent : T.border; e.currentTarget.style.transform = "none"; }}>
                <div style={{ position: "relative" }}>
                  {v.thumbnail && <img src={v.thumbnail} alt={v.title} style={{ width: "100%", aspectRatio: "16/9", objectFit: "cover", display: "block" }} />}
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.3)", opacity: 0, transition: "opacity .2s" }}
                    onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                    onMouseLeave={e => e.currentTarget.style.opacity = "0"}>
                    <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width={18} height={18} viewBox="0 0 24 24" fill="#fff"><polygon points="5,3 19,12 5,21"/></svg>
                    </div>
                  </div>
                </div>
                <div style={{ padding: "8px 10px" }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: T.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.title}</p>
                  <p style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>
                    {v.publishedAt ? new Date(v.publishedAt).toLocaleDateString('en-US') : ''}
                    {v.viewCount ? ` · ${parseInt(v.viewCount).toLocaleString()} views` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents — manufacturing + SharePoint list */}
      {(documents.length > 0 || (listDocs && listDocs.length > 0)) && (
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 14 }}>
            <Icon n="db" size={14} /> Documents & Files
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[...documents, ...(listDocs||[])].map((doc, i) => {
              const Tag = doc.url ? "a" : "div";
              const linkProps = doc.url ? { href: doc.url, target: "_blank", rel: "noopener noreferrer" } : {};
              return (
                <Tag key={i} {...linkProps}
                  style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 14px", textDecoration: "none", display: "flex", alignItems: "flex-start", gap: 10, transition: "border-color .2s", cursor: doc.url ? "pointer" : "default", opacity: doc.url ? 1 : 0.85 }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = doc.url ? T.accent : T.border}
                  onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>
                    {(doc.name||'').endsWith('.pdf') ? '📄' : (doc.name||'').match(/\.(dwg|dxf|stp|step|ipt|iam|idw)$/i) ? '📐' : (doc.name||'').match(/\.(xlsx|csv)$/i) ? '📊' : '📁'}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 13, color: T.textPrimary, fontWeight: 500, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name}</p>
                    {doc.source && <span style={{ fontSize: 10, color: T.textMuted, background: T.border, borderRadius: 4, padding: "1px 6px" }}>{doc.source}</span>}
                    {doc.preview && <p style={{ fontSize: 10, color: T.textMuted, marginTop: 4, fontFamily: "'JetBrains Mono',monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.preview.slice(0, 80)}</p>}
                  </div>
                  {doc.url && <Icon n="external" size={13} color={T.textMuted} />}
                </Tag>
              );
            })}
          </div>
        </div>
      )}

      {videos.length === 0 && documents.length === 0 && (!listDocs || listDocs.length === 0) && (
        <div style={{ ...card, textAlign: "center", color: T.textMuted, padding: 24 }}>
          <Icon n="external" size={20} color={T.border} />
          <p style={{ fontSize: 12, marginTop: 10 }}>No related videos or documents found for this order</p>
        </div>
      )}
    </div>
  );
}

// ─── Alternative Reports config ───────────────────────────────────────────────
// tenantMatch: substring of the tenant name as stored in the DB (case-insensitive)
// status: "published" = visible to the tenant's users.
//         "draft"     = only the PVBI developer (see isDeveloper) can see/open it,
//                       shown with a DRAFT badge. Build & preview a report against
//                       real production data, then flip to "published" and redeploy
//                       to release it to the client. Default (missing) = published.
const ALT_REPORTS = [
  { id: "rdp-financials-q1-2026", name: "RDP Financials Q1 2026", description: "Raindrop Products financial summary — Q1 2026", type: "html", file: "/reports/RDP_Financials_Q1_2026.html", tenant: "RDP", tenantMatch: "rain-drop", status: "published" },
  { id: "analytics-dashboard", name: "Analytics Dashboard", description: "Live charts — monthly revenue, GP%, top customers, salesperson & YoY, plus commission payout report", type: "analytics", tenant: "RDP", tenantMatch: "rain-drop", status: "published" },
  { id: "eew-executive-sales", name: "Executive Sales Report", description: "East End Welding — monthly sales, YoY trends, top customers, open orders & salesperson breakdown", type: "html", file: "/reports/EEW_Executive_Sales_Report.html", tenant: "EEW", tenantMatch: "east end", status: "published" },
  { id: "bio-ecommerce-report", name: "eCommerce & Shipping Report", description: "Biolongevity Labs — order volume, revenue trends, carrier performance, geographic distribution & payment mix", type: "html", file: "/reports/BIO_Ecommerce_Report.html", tenant: "BIO", tenantMatch: "biolongevity", status: "published" },
  { id: "bio-inventory-report", name: "Inventory & Velocity Report", description: "Live inventory levels, 30-day sell-through velocity, days/months of stock available, and open purchase orders by product category", type: "html", file: "/reports/BIO_Inventory_Report.html", tenant: "BIO", tenantMatch: "biolongevity", status: "published" },
  { id: "bio-ghost-orders", name: "Ghost Orders Report", description: "ShipHero fulfilled orders with no tracking number or zero quantity shipped — identify fulfilment gaps before they become customer issues", type: "html", file: "/reports/BIO_Ghost_Orders.html", tenant: "BIO", tenantMatch: "biolongevity", status: "published" },
];

// The PVBI operator who builds reports. Sees "draft" reports (even while using
// "View As" a client tenant); real client users never do. Matches the same
// identity used to gate the Admin panel.
const isDeveloper = (email) => (email || "").toLowerCase().includes("christiaan.horn");

function Dashboard({ user, token, onLogout }) {
  const [nav, setNav] = useState("reports");
  const [altReport, setAltReport] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [soQuery, setSoQuery] = useState("");
  const [soData, setSoData] = useState(null);
  const [soLoading, setSoLoading] = useState(false);
  const [soErr, setSoErr] = useState("");
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsErr, setAnalyticsErr] = useState("");
  const [reports, setReports] = useState([]);
  const [tenantName, setTenantName] = useState("");
  const [loadingReports, setLoadingReports] = useState(true);
  const [reportsErr, setReportsErr] = useState("");
  const [activeReport, setActiveReport] = useState(null);
  const [embedData, setEmbedData] = useState(null);
  const [loadingEmbed, setLoadingEmbed] = useState(false);
  const [embedErr, setEmbedErr] = useState("");
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
const [messages, setMessages] = useState([]);
const [chatHistory, setChatHistory] = useState([]);
const [savedQueries, setSavedQueries] = useState([]);
const [showHistory, setShowHistory] = useState(false);
const [alerts, setAlerts] = useState([]);
const [schedules, setSchedules] = useState([]);
const [auditLogs, setAuditLogs] = useState([]);
const [reportComments, setReportComments] = useState([]);
const [newComment, setNewComment] = useState("");
const [showAlertModal, setShowAlertModal] = useState(false);
const [showScheduleModal, setShowScheduleModal] = useState(false);
const [newAlert, setNewAlert] = useState({ name:"", question:"", condition:"gt", threshold:"", notifyEmail:"", frequency:"daily" });
const [newSchedule, setNewSchedule] = useState({ reportName:"", question:"", recipients:"", frequency:"weekly", dayOfWeek:1, hour:8 });
const [kpiCards, setKpiCards] = useState([]);
const [kpiLoading, setKpiLoading] = useState(false);
const [defaultKpiCards, setDefaultKpiCards] = useState([]);
const [defaultKpiLoading, setDefaultKpiLoading] = useState(false);
const [viewAs, setViewAs] = useState(null);
const [exportingFormat, setExportingFormat] = useState(null);
const [showShareModal, setShowShareModal] = useState(false);
const [shareEmail, setShareEmail] = useState("");
const [shareNote, setShareNote] = useState("");
const [shareSending, setShareSending] = useState(false);
const pbiReportRef = useRef(null);
const [expandedTenant, setExpandedTenant] = useState(null);
const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
const [showAddUser, setShowAddUser] = useState(false);
const [addUserTenantId, setAddUserTenantId] = useState("");
const [addUserTenantName, setAddUserTenantName] = useState("");
const [newUser, setNewUser] = useState({ name: "", email: "", role: "VIEWER" });
const [inviteLoading, setInviteLoading] = useState(false);
const [adminData, setAdminData] = useState(null);
const [showAddTenant, setShowAddTenant] = useState(false);
const inviteUser = async () => {
  if (!newUser.name || !newUser.email) return;
  setInviteLoading(true);
  try {
    const res = await fetch(`${API_BASE}/admin/users`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId: addUserTenantId, ...newUser }),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    setShowAddUser(false);
    setNewUser({ name: "", email: "", role: "VIEWER" });
    loadAdminData();
    alert(`✅ Invitation sent to ${newUser.email}!`);
  } catch (err) {
    alert(`❌ Error: ${err.message}`);
  } finally {
    setInviteLoading(false);
  }
};
const [newTenant, setNewTenant] = useState({
  name: "",
  slug: "",
  azureTenantId: "",
  pbiWorkspaceId: "",
  dbType: "client",
  dbConnectionString: "",
  dbSchema: "public"
});
const [input, setInput] = useState("");
const [aiLoading, setAiLoading] = useState(false);
const messagesEndRef = useRef(null);
useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, aiLoading]);

// Deep-link routing: portal.powervisionbi.com/#report/{id}
useEffect(() => {
  const hash = window.location.hash;
  if (!hash.startsWith('#report/')) return;
  const reportId = decodeURIComponent(hash.replace('#report/', ''));
  if (!reportId || !reports.length) return;
  const found = reports.find(r => r.pbiReportId === reportId || r.id === reportId);
  if (found) { setNav("reports"); openReport(found); window.location.hash = ''; }
}, [reports]);
const loadAdminData = async () => {
  try {
    const res = await fetch(`${API_BASE}/admin/tenants`, {
      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}` }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error("Admin data error:", res.status, err);
      setAdminData({ _error: err.error || `HTTP ${res.status}` });
      return;
    }
    const data = await res.json();
    setAdminData(data);
  } catch (err) {
    console.error("Admin data error:", err);
    setAdminData({ _error: err.message || "Network error" });
  }
};

// ── Load helpers for new features ────────────────────────────────────────────
const loadChatHistory = async () => {
  try { const d = await (await fetch(`${API_BASE}/chat-history`, { headers: authHeaders(token, viewAs?.id) })).json(); setChatHistory(d.messages || []); } catch {}
};
const loadSavedQueries = async () => {
  try { const d = await (await fetch(`${API_BASE}/saved-queries`, { headers: authHeaders(token, viewAs?.id) })).json(); setSavedQueries(d.queries || []); } catch {}
};
const loadAlerts = async () => {
  try { const d = await (await fetch(`${API_BASE}/alerts`, { headers: authHeaders(token, viewAs?.id) })).json(); setAlerts(d.alerts || []); } catch {}
};
const loadSchedules = async () => {
  try { const d = await (await fetch(`${API_BASE}/scheduled-reports`, { headers: authHeaders(token, viewAs?.id) })).json(); setSchedules(d.schedules || []); } catch {}
};
const loadAuditLogs = async () => {
  try { const d = await (await fetch(`${API_BASE}/audit-log`, { headers: authHeaders(token) })).json(); setAuditLogs(d.logs || []); } catch {}
};
const loadComments = async (pbiReportId) => {
  try { const d = await (await fetch(`${API_BASE}/comments?reportId=${encodeURIComponent(pbiReportId)}`, { headers: authHeaders(token, viewAs?.id) })).json(); setReportComments(d.comments || []); } catch {}
};
const logAction = async (action, detail) => {
  try { await fetch(`${API_BASE}/audit-log`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ action, detail }) }); } catch {}
};
const saveMessage = async (role, content, sql, rowCount) => {
  try { await fetch(`${API_BASE}/chat-history`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ role, content, sql, rowCount }) }); } catch {}
};
const DEFAULT_KPIS = [
  { id: 'ytd-rev',    name: 'YTD Revenue',       question: 'What is the total Year-to-Date revenue or net sales amount?',                          color: '#c47a10', icon: 'chart' },
  { id: 'mtd-rev',    name: 'MTD Revenue',       question: 'What is the total Month-to-Date revenue or net sales amount for the current month?',   color: '#c47a10', icon: 'chart' },
  { id: 'ytd-bud',    name: 'YTD Budget',        question: 'What is the Year-to-Date budget or target sales amount?',                              color: '#2ea043', icon: 'zap'   },
  { id: 'mtd-bud',    name: 'MTD Budget',        question: 'What is the Month-to-Date budget or target sales amount for the current month?',       color: '#2ea043', icon: 'zap'   },
  { id: 'ytd-gp',     name: 'YTD GP%',           question: 'What is the Year-to-Date gross profit percentage?',                                    color: '#a78bfa', icon: 'db'    },
  { id: 'mtd-gp',     name: 'MTD GP%',           question: 'What is the Month-to-Date gross profit percentage for the current month?',             color: '#a78bfa', icon: 'db'    },
  { id: 'open-ord',   name: 'Open Orders',       question: 'What is the total COUNT (number) of open confirmed sales orders? Return only the count, not a dollar amount.',  color: '#f59e0b', icon: 'grid'  },
  { id: 'open-ord-val', name: 'Open Order Balance', question: 'What is the total value or dollar amount of all open sales orders?',               color: '#38bdf8', icon: 'send'  },
  { id: 'open-inv',   name: 'Open Invoices',     question: 'What is the total outstanding balance or dollar amount owed on all open unpaid customer invoices?', color: '#f87171', icon: 'alert' },
];

const loadDefaultKPIs = async (noCache = false) => {
  setDefaultKpiLoading(true);
  setDefaultKpiCards([]);
  const results = [];
  for (const kpi of DEFAULT_KPIS) {
    try {
      const res = await fetch(`${API_BASE}/ai-query`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ question: kpi.question, noCache }) });
      const d = await res.json();
      results.push({ ...kpi, answer: d.answer });
    } catch {
      results.push({ ...kpi, answer: null });
    }
    setDefaultKpiCards([...results]);
  }
  setDefaultKpiLoading(false);
};

const loadHomeKPIs = async () => {
  if (!savedQueries.length) return;
  setKpiLoading(true);
  const cards = [];
  for (const q of savedQueries.slice(0, 4)) {
    try {
      const res = await fetch(`${API_BASE}/ai-query`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ question: q.question }) });
      const d = await res.json();
      cards.push({ name: q.name, question: q.question, answer: d.answer, rowCount: d.rowCount });
    } catch {}
  }
  setKpiCards(cards);
  setKpiLoading(false);
};

const addTenant = async () => {
  try {
    await fetch(`${API_BASE}/admin/tenants`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
      body: JSON.stringify(newTenant)
    });
    setShowAddTenant(false);
    setNewTenant({ name: "", slug: "", azureTenantId: "", pbiWorkspaceId: "", dbType: "client", dbConnectionString: "", dbSchema: "public" });
    loadAdminData();
  } catch (err) {
    console.error("Add tenant error:", err);
  }
};
const sendMessage = async (override) => {
  const question = (override ?? input).trim();
  if (!question || aiLoading) return;
  if (!override) setInput("");
  const userMsg = { id: Date.now(), role: "user", content: question };
  const history = [...messages, userMsg];
  setMessages(history);
  saveMessage("user", question);
  sendAiRequest(question, history);
};

const sendAiRequest = async (question, history) => {
  setAiLoading(true);
  try {
    const res = await fetch(`${API_BASE}/ai-query`, {
      method: "POST",
      headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" },
      body: JSON.stringify({ question, history: history.map(m => ({ role: m.role, content: m.content })) }),
    });
    const data = await res.json();
    const assistantMsg = { id: Date.now(), role: "assistant", content: data.answer || "Sorry, I could not answer that.", chart: data.chart || null, exportData: data.exportData || null, question };
    setMessages(prev => [...prev, assistantMsg]);
    saveMessage("assistant", assistantMsg.content, data.sql, data.rowCount);
    logAction("ai_query", question);
  } catch {
    setMessages(prev => [...prev, { id: Date.now(), role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
  } finally {
    setAiLoading(false);
  }
};
 useEffect(() => {
  let cancelled = false;
  async function load() {
    setLoadingReports(true); setReportsErr("");
    try {
      const data = await fetchReports(token, viewAs?.id);
      if (!cancelled) {
        setReports(data.reports || []);
        setTenantName(data.tenant?.name || "");
      }
    } catch (e) {
      if (!cancelled) setReportsErr("Failed to load reports. Please refresh or contact support.");
    } finally {
      if (!cancelled) setLoadingReports(false);
    }
    // Load admin data if admin user
    if (user.email?.toLowerCase().includes("christiaan.horn")) {
      loadAdminData();
    }
  }
  load();
  return () => { cancelled = true; };
}, [token, viewAs]);

// Load feature data on mount
useEffect(() => {
  if (!token) return;
  loadSavedQueries();
  loadChatHistory();
  loadAlerts();
  loadSchedules();
}, [token, viewAs]);

// Mirror the impersonation target into sessionStorage so the module-level
// crmApi() helper can scope CRM calls to the tenant being viewed.
useEffect(() => {
  if (viewAs?.id) sessionStorage.setItem("pvbi_impersonate", viewAs.id);
  else sessionStorage.removeItem("pvbi_impersonate");
}, [viewAs]);

// Load comments when report opens
useEffect(() => {
  if (activeReport) { loadComments(activeReport.pbiReportId || activeReport.id); logAction('view_report', activeReport.name); }
}, [activeReport]);

// Load KPIs when on home tab
useEffect(() => {
  if (nav === 'home') {
    loadDefaultKPIs();
    if (savedQueries.length) loadHomeKPIs();
  }
}, [nav]);
useEffect(() => { if (nav === 'audit') loadAuditLogs(); }, [nav]);
useEffect(() => { if (nav === 'alerts') loadAlerts(); }, [nav]);
useEffect(() => { if (nav === 'schedule') loadSchedules(); }, [nav]);
useEffect(() => { if (nav === 'admin') loadAdminData(); }, [nav]);

  // Open a report — fetch embed token from backend
  const openReport = useCallback(async (report) => {
    setActiveReport(report);
    setEmbedData(null);
    setEmbedErr("");
    setLoadingEmbed(true);
    try {
      const data = await fetchEmbedToken(token, report.id, viewAs?.id);
      setEmbedData(data);
    } catch (e) {
      setEmbedErr("Could not load this report. Please try again.");
    } finally {
      setLoadingEmbed(false);
    }
  }, [token, viewAs]);

  const activeTenantName = (viewAs?.name || tenantName || "").toLowerCase();
  const canSeeDrafts = isDeveloper(user.email);
  const altReportsForTenant = ALT_REPORTS.filter(r =>
    activeTenantName.includes(r.tenantMatch) &&
    (r.status !== "draft" || canSeeDrafts)
  );

  const cats = ["All", ...Array.from(new Set(reports.map(r => r.cat || "Other")))];
  const filtered = reports.filter(r => {
    const ms = r.name.toLowerCase().includes(search.toLowerCase()) || (r.description || "").toLowerCase().includes(search.toLowerCase());
    return ms && (cat === "All" || r.cat === cat);
  });
  const initials = (user.name || "U").slice(0, 2).toUpperCase();

  return (
    <div style={{ display: "flex", minHeight: "100vh", position: "relative", zIndex: 1 }}>
      {/* Mobile overlay */}
      <div className={`sidebar-overlay${mobileSidebarOpen ? " open" : ""}`} onClick={() => setMobileSidebarOpen(false)} />
      {/* Fixed mobile hamburger — always visible on small screens */}
      {!mobileSidebarOpen && (
        <button className="hamburger-btn" onClick={() => setMobileSidebarOpen(true)}
          style={{ position: "fixed", bottom: 20, right: 20, zIndex: 300, width: 48, height: 48, borderRadius: "50%", background: T.accent, border: "none", color: "#fff", boxShadow: "0 4px 16px rgba(0,0,0,.4)" }}>
          <Icon n="menu" size={20} color="#fff" />
        </button>
      )}
      {/* Sidebar */}
      <div className={`mob-sidebar${mobileSidebarOpen ? " open" : ""}`} style={{ width: 224, background: T.surface, borderRight: `1px solid ${T.border}`, display: "flex", flexDirection: "column", padding: "18px 12px", flexShrink: 0 }}>
        <div style={{ padding: "4px 8px 8px 8px", marginBottom: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em", color: T.textPrimary }}>
            Power<span style={{ color: T.accent }}>Vision</span> BI
          </div>
          <p style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>We Make Data Dance</p>
        </div>
        <div style={{ height: 1, background: `linear-gradient(90deg, ${T.accent}60, transparent)`, marginBottom: 20, marginLeft: 8, marginRight: 8 }} />

        <nav style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
          {[
{ id: "home", icon: "home", label: "Home" },
{ id: "reports", icon: "grid", label: "Power BI" },
...(altReportsForTenant.length > 0 ? [{ id: "alt-reports", icon: "external", label: "Reports" }] : []),
...(activeTenantName.includes("rain-drop") ? [{ id: "order-lookup", icon: "db", label: "Order Lookup" }] : []),
{ id: "ai", icon: "zap", label: "AI Agent" },
{ id: "crm", icon: "users", label: "CRM" },
{ id: "alerts", icon: "alert", label: "Alerts" },
{ id: "schedule", icon: "refresh", label: "Scheduled" },
{ id: "audit", icon: "eye", label: "Audit Log" },
{ id: "settings", icon: "settings", label: "Settings" },
...(isDeveloper(user.email) ? [{ id: "admin", icon: "shield", label: "Admin" }] : []),
].map(item => (
            <button key={item.id} className={`nav-item ${nav === item.id ? "active" : ""}`} onClick={() => { setNav(item.id); setActiveReport(null); setAltReport(null); setMobileSidebarOpen(false); }}>
              <Icon n={item.icon} size={15} />
              {item.label}
            </button>
          ))}
        </nav>

        {/* User */}
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 8px", borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: `linear-gradient(135deg, ${T.accent}, ${T.accentDark})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name}</p>
            <p style={{ fontSize: 10, color: T.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</p>
          </div>
          <button onClick={onLogout} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, display: "flex", padding: 4 }} title="Sign out">
            <Icon n="logout" size={14} />
          </button>
        </div>
      </div>

      {/* Impersonation banner */}
      {viewAs && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 999, background: `linear-gradient(90deg, ${T.accent}, ${T.accentDark})`, padding: "8px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>
            👁 Viewing as: <strong>{viewAs.name}</strong>
          </span>
          <button
            onClick={() => { setViewAs(null); setMessages([]); setActiveReport(null); setNav("reports"); }}
            style={{ background: "rgba(255,255,255,.2)", border: "none", borderRadius: 6, padding: "4px 14px", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            ✕ Exit client view
          </button>
        </div>
      )}

      {/* Main */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", marginTop: viewAs ? 36 : 0 }}>

        {/* ── Home Dashboard ── */}
        {nav === "home" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px 36px" }}>

            {/* Header */}
            <div className="mob-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button className="hamburger-btn" onClick={() => setMobileSidebarOpen(true)}><Icon n="menu" size={18} /></button>
                <div>
                  <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>
                    Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, {user.name?.split(" ")[0] || user.name}
                  </h1>
                  <p style={{ fontSize: 13, color: T.textSecondary }}>Live business KPIs from your connected database</p>
                </div>
              </div>
              <button onClick={() => { loadDefaultKPIs(true); if (savedQueries.length) loadHomeKPIs(); }}
                disabled={defaultKpiLoading} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 16px", color: T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", display: "flex", alignItems: "center", gap: 6 }}>
                <Icon n="refresh" size={13} />{defaultKpiLoading ? "Loading…" : "Refresh all"}
              </button>
            </div>

            {/* ── Default KPI grid ── */}
            <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 36 }}>
              {DEFAULT_KPIS.map(kpi => {
                const card = defaultKpiCards.find(c => c.id === kpi.id);
                const val = card ? extractKpiValue(card.answer) : null;
                return (
                  <div key={kpi.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "20px 22px 16px", position: "relative", overflow: "hidden", transition: "border-color .2s, box-shadow .2s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = kpi.color; e.currentTarget.style.boxShadow = `0 6px 24px ${kpi.color}20`; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; }}>
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: kpi.color, borderRadius: "14px 14px 0 0" }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: T.textMuted }}>{kpi.name}</p>
                      <div style={{ color: kpi.color, opacity: 0.7 }}><Icon n={kpi.icon} size={15} /></div>
                    </div>
                    {defaultKpiLoading && !card ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, height: 44 }}>
                        <span className="spinner spin" style={{ width: 15, height: 15, borderTopColor: kpi.color, borderColor: `${kpi.color}33` }} />
                        <span style={{ fontSize: 12, color: T.textMuted }}>Loading…</span>
                      </div>
                    ) : (
                      <p style={{ fontSize: 30, fontWeight: 700, color: kpi.color, lineHeight: 1.1, fontFamily: "'JetBrains Mono', monospace" }}>
                        {val || "—"}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── Custom KPI cards (saved queries) ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>My KPIs <span style={{ fontWeight: 400, color: T.textMuted }}>— saved from AI Agent</span></p>
              <button className="btn-secondary" style={{ fontSize: 12, padding: "6px 14px" }} onClick={() => setNav("ai")}>+ Add KPI</button>
            </div>
            {savedQueries.length === 0 ? (
              <div style={{ background: T.surface, border: `1px dashed ${T.border}`, borderRadius: 12, padding: "28px 24px", textAlign: "center", color: T.textMuted }}>
                <Icon n="starEmpty" size={22} color={T.border} />
                <p style={{ fontSize: 13, marginTop: 10 }}>No custom KPIs yet</p>
                <p style={{ fontSize: 12, marginTop: 4 }}>Ask the AI Agent anything, then click ☆ to pin it here</p>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 14 }}>
                {savedQueries.map((q, i) => {
                  const card = kpiCards.find(c => c.question === q.question);
                  const CARD_COLORS = [T.accent, T.success, "#a78bfa", T.warn, "#f87171", "#38bdf8"];
                  const color = CARD_COLORS[i % CARD_COLORS.length];
                  const val = card ? extractKpiValue(card.answer) : null;
                  return (
                    <div key={q.id} onClick={() => setNav("ai")} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "20px 20px 16px", cursor: "pointer", transition: "border-color .2s, box-shadow .2s, transform .2s", position: "relative", overflow: "hidden" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `0 6px 24px ${color}22`; e.currentTarget.style.transform = "translateY(-2px)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>
                      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: color, borderRadius: "14px 14px 0 0" }} />
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                        <p style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>{q.name}</p>
                        <button onClick={async e => { e.stopPropagation(); e.preventDefault(); const res = await fetch(`${API_BASE}/saved-queries`, { method: "DELETE", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: q.id }) }); if (res.ok) setSavedQueries(prev => prev.filter(sq => sq.id !== q.id)); }} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 2 }}><Icon n="trash" size={13} /></button>
                      </div>
                      {kpiLoading && !card ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, height: 44 }}>
                          <span className="spinner spin" style={{ width: 14, height: 14, borderTopColor: color, borderColor: `${color}33` }} />
                          <span style={{ fontSize: 12, color: T.textMuted }}>Loading…</span>
                        </div>
                      ) : card ? (
                        <p style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1.1, fontFamily: "'JetBrains Mono', monospace" }}>
                          {val || "—"}
                        </p>
                      ) : (
                        <p style={{ fontSize: 12, color: T.textMuted, height: 44, display: "flex", alignItems: "center" }}>Click Refresh to load</p>
                      )}
                      <p style={{ fontSize: 11, color: T.textMuted, marginTop: 10, borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>{q.question}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {nav === "reports" && !activeReport && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div className="fu" style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Your reports</h1>
              <p style={{ fontSize: 13, color: T.textSecondary }}>Live Power BI reports — click any report to open it</p>
            </div>

            {/* Stats */}
            <div className="fu1 mob-stats" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 13, marginBottom: 28 }}>
              {[
                { label: "Reports available", value: loadingReports ? "…" : reports.length, icon: "grid", color: T.accent },
                { label: "Tenant", value: viewAs?.name || tenantName || "Loading…", icon: "db", color: T.success },
                { label: "Session status", value: "Active", icon: "refresh", color: T.warn },
              ].map((s, i) => (
                <div key={i} className="stat-card" style={{ animationDelay: `${i * 0.08}s` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <p style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>{s.label}</p>
                      <p style={{ fontSize: 20, fontWeight: 600, color: s.color }}>{s.value}</p>
                    </div>
                    <div style={{ color: s.color, opacity: 0.7 }}><Icon n={s.icon} size={18} /></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Error state */}
            {reportsErr && <div className="alert alert-error" style={{ marginBottom: 20 }}><Icon n="alert" size={15} color={T.error} />{reportsErr}</div>}

            {/* Loading state */}
            {loadingReports && (
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 32, color: T.textSecondary }}>
                <span className="spinner spin" style={{ width: 20, height: 20, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />
                Loading your reports…
              </div>
            )}

            {!loadingReports && !reportsErr && (
              <>
                {/* Filters */}
                <div className="fu2" style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
                  <input className="field-input" style={{ maxWidth: 240, padding: "9px 14px" }} placeholder="Search reports…" value={search} onChange={e => setSearch(e.target.value)} />
                  <div style={{ display: "flex", gap: 6 }}>
                    {cats.map(c => (
                      <button key={c} onClick={() => setCat(c)} style={{ padding: "7px 14px", borderRadius: 20, border: `1px solid ${cat === c ? T.accent : T.border}`, background: cat === c ? T.accentGlow : "transparent", color: cat === c ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontWeight: 500, fontFamily: "'Plus Jakarta Sans', sans-serif", transition: "all .15s" }}>{c}</button>
                    ))}
                  </div>
                </div>

                {/* Report grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
                  {filtered.map((r, i) => (
                    <div key={r.id} className="report-card" style={{ animationDelay: `${.1 + i * .06}s` }} onClick={() => openReport(r)}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                        {r.cat && <span className="badge" style={{ background: `${CAT_COLOR[r.cat] || T.accent}18`, color: CAT_COLOR[r.cat] || T.accent, border: `1px solid ${CAT_COLOR[r.cat] || T.accent}30`, fontSize: 10 }}>{r.cat}</span>}
                        {r.updated && <span style={{ fontSize: 11, color: T.textMuted }}>{r.updated}</span>}
                      </div>
                      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{r.name}</h3>
                      <p style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.55 }}>{r.description}</p>
                      <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 5, color: T.accent, fontSize: 12, fontWeight: 500 }}>
                        <Icon n="external" size={13} /> Open report
                      </div>
                    </div>
                  ))}
                  {filtered.length === 0 && !loadingReports && (
                    <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 48, color: T.textMuted }}>
                      <Icon n="chart" size={32} color={T.border} />
                      <p style={{ marginTop: 12, fontSize: 14 }}>No reports found</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Power BI Report Viewer ── */}
        {nav === "reports" && activeReport && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "24px 32px", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button className="btn-secondary" style={{ padding: "8px 14px" }} onClick={() => { setActiveReport(null); setEmbedData(null); }}>← Back</button>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontSize: 18, fontWeight: 600 }}>{activeReport.name}</h2>
                <p style={{ fontSize: 12, color: T.textSecondary }}>{activeReport.description}</p>
              </div>
              <span className="badge badge-success"><span style={{ width: 5, height: 5, borderRadius: "50%", background: T.success }} />Live data</span>
              <button
                onClick={async () => {
                  if (pbiReportRef.current?.print) {
                    try { await pbiReportRef.current.print(); } catch { window.print(); }
                  } else { window.print(); }
                }}
                style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 12px", color: T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap" }}>
                ↓ PDF
              </button>

              {/* Share by email */}
              <button
                onClick={() => { setShowShareModal(true); setShareEmail(""); setShareNote(""); }}
                style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 12px", color: T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap" }}>
                ✉ Share
              </button>
            </div>

            {/* Share modal */}
            {showShareModal && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                <div className="card" style={{ width: 440, padding: 28 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Share report</h2>
                  <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 20 }}>{activeReport?.name}</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div className="field-group">
                      <label className="field-label">Recipient email</label>
                      <input className="field-input" type="email" placeholder="colleague@company.com" value={shareEmail} onChange={e => setShareEmail(e.target.value)} />
                    </div>
                    <div className="field-group">
                      <label className="field-label">Personal note (optional)</label>
                      <textarea className="field-input" rows={3} placeholder="Here's the report you asked about..." value={shareNote} onChange={e => setShareNote(e.target.value)} style={{ resize: "vertical", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13 }} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                    <button
                      className="btn-primary"
                      disabled={shareSending || !shareEmail.trim()}
                      onClick={async () => {
                        setShareSending(true);
                        try {
                          const res = await fetch(`${API_BASE}/email-report`, {
                            method: "POST",
                            headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" },
                            body: JSON.stringify({
                              reportId: activeReport?.pbiReportId || activeReport?.id,
                              reportName: activeReport?.name,
                              reportDescription: activeReport?.description,
                              recipientEmail: shareEmail.trim(),
                              note: shareNote.trim(),
                            }),
                          });
                          if (res.ok) {
                            setShowShareModal(false);
                            alert(`Report shared with ${shareEmail} ✓`);
                          } else {
                            const e = await res.json();
                            alert("Failed: " + (e.error || "Unknown error"));
                          }
                        } catch { alert("Send failed. Please try again."); }
                        finally { setShareSending(false); }
                      }}
                      style={{ flex: 1 }}>
                      {shareSending ? "Sending…" : "Send email"}
                    </button>
                    <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowShareModal(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            <div style={{ flex: 1, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: T.surface, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 500 }}>

              {loadingEmbed && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, color: T.textSecondary }}>
                  <span className="spinner spin" style={{ width: 28, height: 28, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />
                  <p style={{ fontSize: 14 }}>Loading report…</p>
                </div>
              )}

              {embedErr && (
                <div style={{ textAlign: "center", padding: 32 }}>
                  <div className="alert alert-error" style={{ display: "inline-flex" }}><Icon n="alert" size={15} color={T.error} />{embedErr}</div>
                  <br /><br />
                  <button className="btn-secondary" onClick={() => openReport(activeReport)}>Retry</button>
                </div>
              )}

              {/* Power BI Embedded iframe — uses the token from /api/embed-token */}
             {embedData && !loadingEmbed && (
  <div
    id="report-print-frame"
    ref={el => {
      if (el && embedData && window.powerbi) {
        window.powerbi.reset(el);
        pbiReportRef.current = window.powerbi.embed(el, {
          type: 'report',
          id: embedData.reportId,
          embedUrl: embedData.embedUrl,
          accessToken: embedData.token,
          tokenType: window.models?.TokenType?.Embed ?? 1,
          settings: {
            navContentPaneEnabled: true,
            filterPaneEnabled: true,
            exportDataEnabled: true,
          }
        });
      }
    }}
    style={{ width: "100%", height: "100%", minHeight: 600 }}
  />
)}
            </div>

            {/* Comments panel */}
            <div style={{ borderTop: `1px solid ${T.border}`, padding: 16 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 10 }}>Comments ({reportComments.length})</p>
              <div style={{ maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                {reportComments.map(c => (
                  <div key={c.id} style={{ background: T.bg, borderRadius: 6, padding: "8px 10px", fontSize: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                      <span style={{ fontWeight: 600, color: T.accent }}>{c.user?.name || c.user?.email}</span>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ color: T.textMuted, fontSize: 11 }}>{new Date(c.createdAt).toLocaleDateString()}</span>
                        <button onClick={async () => { await fetch(`${API_BASE}/comments`, { method: "DELETE", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: c.id }) }); loadComments(activeReport.pbiReportId || activeReport.id); }} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 0 }}><Icon n="x" size={11} /></button>
                      </div>
                    </div>
                    <p style={{ color: T.textPrimary, lineHeight: 1.5 }}>{c.content}</p>
                  </div>
                ))}
                {reportComments.length === 0 && <p style={{ fontSize: 11, color: T.textMuted }}>No comments yet — be the first to add one</p>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input className="field-input" style={{ fontSize: 12 }} placeholder="Add a comment…" value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={async e => { if (e.key === "Enter" && newComment.trim()) { await fetch(`${API_BASE}/comments`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ pbiReportId: activeReport.pbiReportId || activeReport.id, reportName: activeReport.name, content: newComment.trim() }) }); setNewComment(""); loadComments(activeReport.pbiReportId || activeReport.id); }}} />
                <button className="btn-secondary" style={{ whiteSpace: "nowrap", padding: "8px 14px" }} onClick={async () => { if (!newComment.trim()) return; await fetch(`${API_BASE}/comments`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ pbiReportId: activeReport.pbiReportId || activeReport.id, reportName: activeReport.name, content: newComment.trim() }) }); setNewComment(""); loadComments(activeReport.pbiReportId || activeReport.id); }}>Post</button>
              </div>
            </div>
          </div>
        )}
{/* ── Alternative Reports ── */}
{nav === "alt-reports" && !altReport && (
  <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
    <div className="fu" style={{ marginBottom: 28 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Alternative Reports</h1>
      <p style={{ fontSize: 13, color: T.textSecondary }}>Interactive reports — click any report to open it</p>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
      {altReportsForTenant.map((r, i) => (
        <div key={r.id} className="report-card" style={{ animationDelay: `${.1 + i * .06}s` }} onClick={() => { setAltReport(r); if (r.type === "analytics") { setAnalyticsData(null); setAnalyticsLoading(true); setAnalyticsErr(""); fetch(`${API_BASE}/analytics`, { headers: authHeaders(token, viewAs?.id) }).then(res=>res.json()).then(d=>{ if(d.error) throw new Error(d.error); setAnalyticsData(d); }).catch(e=>setAnalyticsErr(e.message)).finally(()=>setAnalyticsLoading(false)); } }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <span className="badge badge-info">{r.tenant}</span>
            {r.status === "draft" && (
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".04em", color: "#b45309", background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 6, padding: "2px 8px" }}>DRAFT</span>
            )}
          </div>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{r.name}</h3>
          <p style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.55 }}>{r.description}</p>
          <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 5, color: T.accent, fontSize: 12, fontWeight: 500 }}>
            <Icon n="external" size={13} /> Open report
          </div>
        </div>
      ))}
    </div>
  </div>
)}
{nav === "alt-reports" && altReport && (
  <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "24px 32px", gap: 16, overflow: altReport.type === "analytics" ? "auto" : "hidden" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
      <button className="btn-secondary" style={{ padding: "8px 14px" }} onClick={() => { setAltReport(null); setAnalyticsData(null); }}>← Back</button>
      <div style={{ flex: 1 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>{altReport.name}</h2>
        <p style={{ fontSize: 12, color: T.textSecondary }}>{altReport.description}</p>
      </div>
      <span className="badge badge-info">{altReport.tenant}</span>
      {altReport.status === "draft" && (
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".04em", color: "#b45309", background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 6, padding: "2px 8px" }}>DRAFT — not visible to client</span>
      )}
      {altReport.type === "analytics" && (
        <button onClick={() => { setAnalyticsData(null); setAnalyticsLoading(true); setAnalyticsErr(""); fetch(`${API_BASE}/analytics`, { headers: authHeaders(token, viewAs?.id) }).then(r=>r.json()).then(d=>{ if(d.error) throw new Error(d.error); setAnalyticsData(d); }).catch(e=>setAnalyticsErr(e.message)).finally(()=>setAnalyticsLoading(false)); }}
          style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "7px 14px", color: T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans',sans-serif", display: "flex", alignItems: "center", gap: 6 }}>
          <Icon n="refresh" size={13} />Refresh
        </button>
      )}
    </div>
    {altReport.type === "html" && (
      <iframe src={`${altReport.file}?t=${Date.now()}`} style={{ flex: 1, border: "none", borderRadius: 8, background: "#fff", height: "calc(100vh - 120px)", width: "100%" }} title={altReport.name} />
    )}
    {altReport.type === "analytics" && analyticsLoading && (
      <div style={{ display: "flex", alignItems: "center", gap: 12, color: T.textSecondary, padding: 48 }}>
        <span className="spinner spin" style={{ width: 20, height: 20, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />Loading analytics…
      </div>
    )}
    {altReport.type === "analytics" && analyticsErr && <div className="alert alert-error"><Icon n="alert" size={15} color={T.error} />{analyticsErr}</div>}
    {altReport.type === "analytics" && analyticsData && <AnalyticsCharts data={analyticsData} />}
  </div>
)}
{/* ── Order Lookup ── */}
{nav === "order-lookup" && (
  <div style={{ flex: 1, overflow: "auto", padding: "32px 36px" }}>
    <div className="fu" style={{ marginBottom: 24 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Order Lookup</h1>
      <p style={{ fontSize: 13, color: T.textSecondary }}>Enter a sales order number to see everything related — items, invoices, shipments, videos & documents</p>
    </div>

    {/* Search */}
    <div style={{ display: "flex", gap: 10, marginBottom: 28, maxWidth: 500 }}>
      <input className="field-input" placeholder="e.g. 1968514" value={soQuery}
        onChange={e => setSoQuery(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") {
          setSoData(null); setSoErr(""); setSoLoading(true);
          fetch(`${API_BASE}/order-lookup?soNo=${encodeURIComponent(soQuery.trim())}`, { headers: authHeaders(token, viewAs?.id) })
            .then(r => r.json()).then(d => { if (d.error) throw new Error(d.error); setSoData(d); })
            .catch(e => setSoErr(e.message)).finally(() => setSoLoading(false));
        }}}
        style={{ flex: 1, fontSize: 16, fontFamily: "'JetBrains Mono',monospace" }} />
      <button className="btn-primary" style={{ width: "auto", padding: "11px 24px" }}
        disabled={soLoading || !soQuery.trim()}
        onClick={() => {
          setSoData(null); setSoErr(""); setSoLoading(true);
          fetch(`${API_BASE}/order-lookup?soNo=${encodeURIComponent(soQuery.trim())}`, { headers: authHeaders(token, viewAs?.id) })
            .then(r => r.json()).then(d => { if (d.error) throw new Error(d.error); setSoData(d); })
            .catch(e => setSoErr(e.message)).finally(() => setSoLoading(false));
        }}>
        {soLoading ? <><span className="spinner spin" style={{ borderTopColor: "#fff", borderColor: "rgba(255,255,255,.3)" }} /> Searching…</> : "Search"}
      </button>
    </div>

    {soErr && <div className="alert alert-error" style={{ marginBottom: 20 }}><Icon n="alert" size={15} color={T.error} />{soErr}</div>}

    {soData && <SalesOrderDetail data={soData} />}

    {!soData && !soLoading && !soErr && (
      <div style={{ textAlign: "center", padding: "64px 0", color: T.textMuted }}>
        <Icon n="db" size={40} color={T.border} />
        <p style={{ marginTop: 16, fontSize: 14 }}>Enter a sales order number above</p>
        <p style={{ fontSize: 12, marginTop: 6 }}>Shows items, invoices, shipment status, YouTube videos & documents</p>
      </div>
    )}
  </div>
)}
{/* ── AI Agent ── */}
{nav === "ai" && (
  <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "32px" }}>
    <div className="fu" style={{ marginBottom: 20, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>AI Data Agent</h1>
        <p style={{ fontSize: 13, color: T.textSecondary }}>Ask any question about your business data in plain English</p>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => { setShowHistory(!showHistory); }} style={{ background: showHistory ? T.accentGlow2 : "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 12px", color: showHistory ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", display: "flex", alignItems: "center", gap: 5 }}>
          <Icon n="clock" size={13} /> History
        </button>
        <button onClick={async () => { await fetch(`${API_BASE}/chat-history`, { method: "DELETE", headers: authHeaders(token, viewAs?.id) }); setMessages([]); setChatHistory([]); }} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 12px", color: T.textMuted, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Clear
        </button>
      </div>
    </div>

    <div className="card" style={{ flex: 1, display: "flex", flexDirection: "column", maxHeight: "70vh" }}>
      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: 12 }} id="chat-messages">
        {messages.length === 0 && (
          <div style={{ textAlign: "center", padding: 48, color: T.textMuted }}>
            <Icon n="zap" size={32} color={T.border} />
            <p style={{ marginTop: 12, fontSize: 14 }}>Ask me anything about your business data</p>
            <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 8 }}>
              {["What were my top 5 customers last month?", "Show me overdue invoices", "How is my revenue trending?"].map(q => (
                <button key={q} onClick={() => sendMessage(q)} style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.2)`, borderRadius: 8, padding: "8px 16px", color: T.accent, cursor: "pointer", fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{q}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: m.chart ? "98%" : "75%", width: m.chart ? "98%" : undefined, padding: "12px 16px", borderRadius: 12, background: m.role === "user" ? T.accent : T.surface, border: `1px solid ${m.role === "user" ? T.accent : T.border}`, color: m.role === "user" ? "#fff" : T.textPrimary, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {m.content}
              {m.chart && <ChartWidget chart={m.chart} />}
              {m.exportData && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}`, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={() => downloadExcel(m.exportData, m.question)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(34,197,94,.12)", border: "1px solid rgba(34,197,94,.25)", borderRadius: 6, padding: "5px 12px", color: "#22c55e", cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    ↓ Export to Excel ({m.exportData.length.toLocaleString()} rows)
                  </button>
                  {m.role === "assistant" && m.question && (
                    <button onClick={async () => {
                      const name = prompt("Save as KPI card — enter a short name:", m.question.slice(0,40));
                      if (!name) return;
                      await fetch(`${API_BASE}/saved-queries`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ name, question: m.question }) });
                      loadSavedQueries();
                      alert("Saved to Home dashboard ✓");
                    }} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: T.accentGlow2, border: `1px solid rgba(196,122,16,.25)`, borderRadius: 6, padding: "5px 12px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      ☆ Save as KPI
                    </button>
                  )}
                </div>
              )}
              {m.role === "assistant" && m.question && !m.exportData && (
                <div style={{ marginTop: 8 }}>
                  <button onClick={async () => {
                    const name = prompt("Save as KPI card:", m.question.slice(0,40));
                    if (!name) return;
                    await fetch(`${API_BASE}/saved-queries`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ name, question: m.question }) });
                    loadSavedQueries(); alert("Saved to Home dashboard ✓");
                  }} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 0 }}>
                    ☆ Save as KPI
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {aiLoading && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{ padding: "12px 16px", borderRadius: 12, background: T.surface, border: `1px solid ${T.border}` }}>
              <span className="spinner spin" style={{ width: 16, height: 16, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Saved queries quick-access */}
      {savedQueries.length > 0 && (
        <div style={{ borderTop: `1px solid ${T.border}`, padding: "8px 16px", display: "flex", gap: 6, overflowX: "auto" }}>
          {savedQueries.slice(0,5).map(q => (
            <button key={q.id} onClick={() => sendMessage(q.question)} style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.2)`, borderRadius: 20, padding: "4px 12px", color: T.accent, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap", flexShrink: 0 }}>
              {q.name}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{ borderTop: `1px solid ${T.border}`, padding: 16, display: "flex", gap: 10 }}>
        <input className="field-input" placeholder="Ask anything about your data..." value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()} disabled={aiLoading} />
        <button type="button" className="btn-primary" style={{ width: "auto", padding: "11px 20px", whiteSpace: "nowrap" }} onClick={() => sendMessage()} disabled={aiLoading || !input.trim()}>Send</button>
      </div>
    </div>

    {/* History sidebar */}
    {showHistory && (
      <div style={{ width: 280, background: T.surface, borderLeft: `1px solid ${T.border}`, display: "flex", flexDirection: "column", marginTop: -60 }}>
        <div style={{ padding: "16px", borderBottom: `1px solid ${T.border}` }}>
          <p style={{ fontSize: 13, fontWeight: 600 }}>Chat history</p>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
          {chatHistory.filter(m => m.role === "user").map(m => (
            <div key={m.id} onClick={() => sendMessage(m.content)} style={{ padding: "8px 10px", borderRadius: 6, cursor: "pointer", marginBottom: 4, fontSize: 12, color: T.textSecondary, background: T.bg, border: `1px solid ${T.border}` }}
              onMouseEnter={e => e.currentTarget.style.background = T.surfaceHover}
              onMouseLeave={e => e.currentTarget.style.background = T.bg}>
              {m.content.slice(0, 60)}{m.content.length > 60 ? "…" : ""}
            </div>
          ))}
          {chatHistory.filter(m => m.role === "user").length === 0 && <p style={{ fontSize: 12, color: T.textMuted, textAlign: "center", padding: 20 }}>No history yet</p>}
        </div>
      </div>
    )}
  </div>
)}
        {/* ── CRM ── */}
        {nav === "crm" && <CrmApp />}

        {/* ── Alerts ── */}
        {nav === "alerts" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
              <div><h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>KPI Alerts</h1><p style={{ fontSize: 13, color: T.textSecondary }}>Get emailed when a metric crosses a threshold</p></div>
              <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} onClick={() => setShowAlertModal(true)}>+ New Alert</button>
            </div>
            {alerts.length === 0 ? (
              <div style={{ textAlign: "center", padding: 64, color: T.textMuted }}><Icon n="bell" size={32} color={T.border} /><p style={{ marginTop: 12 }}>No alerts set up yet</p></div>
            ) : alerts.map(a => (
              <div key={a.id} className="card" style={{ padding: 16, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <p style={{ fontWeight: 600, marginBottom: 4 }}>{a.name}</p>
                  <p style={{ fontSize: 12, color: T.textSecondary }}>{a.question}</p>
                  <p style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Threshold: {a.condition === "gt" ? ">" : a.condition === "lt" ? "<" : "="} {a.threshold} · {a.frequency} · → {a.notifyEmail}{a.lastValue != null ? ` · Last: ${a.lastValue}` : ""}</p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={async () => { await fetch(`${API_BASE}/alerts`, { method: "PATCH", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: a.id, isActive: !a.isActive }) }); loadAlerts(); }} style={{ background: a.isActive ? "rgba(34,197,94,.12)" : T.bg, border: `1px solid ${a.isActive ? "rgba(34,197,94,.3)" : T.border}`, borderRadius: 6, padding: "4px 10px", color: a.isActive ? "#22c55e" : T.textMuted, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{a.isActive ? "Active" : "Paused"}</button>
                  <button onClick={async () => { await fetch(`${API_BASE}/alerts`, { method: "DELETE", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: a.id }) }); loadAlerts(); }} style={{ background: "none", border: `1px solid rgba(239,68,68,.3)`, borderRadius: 6, padding: "4px 10px", color: "#ef4444", cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Delete</button>
                </div>
              </div>
            ))}
            {showAlertModal && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                <div className="card" style={{ width: 480, padding: 28 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>New KPI Alert</h2>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <input className="field-input" placeholder="Alert name (e.g. High open orders)" value={newAlert.name} onChange={e => setNewAlert(p => ({ ...p, name: e.target.value }))} />
                    <input className="field-input" placeholder="AI question (e.g. how many open sales orders)" value={newAlert.question} onChange={e => setNewAlert(p => ({ ...p, question: e.target.value }))} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <select className="field-input" value={newAlert.condition} onChange={e => setNewAlert(p => ({ ...p, condition: e.target.value }))} style={{ background: T.surface, color: T.textPrimary }}>
                        <option value="gt">Greater than (&gt;)</option><option value="lt">Less than (&lt;)</option><option value="eq">Equal to (=)</option>
                      </select>
                      <input className="field-input" placeholder="Threshold (e.g. 500)" type="number" value={newAlert.threshold} onChange={e => setNewAlert(p => ({ ...p, threshold: e.target.value }))} />
                    </div>
                    <input className="field-input" placeholder="Notify email" type="email" value={newAlert.notifyEmail} onChange={e => setNewAlert(p => ({ ...p, notifyEmail: e.target.value }))} />
                    <select className="field-input" value={newAlert.frequency} onChange={e => setNewAlert(p => ({ ...p, frequency: e.target.value }))} style={{ background: T.surface, color: T.textPrimary }}>
                      <option value="hourly">Check hourly</option><option value="daily">Check daily</option><option value="weekly">Check weekly</option>
                    </select>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                    <button className="btn-primary" style={{ flex: 1 }} onClick={async () => { await fetch(`${API_BASE}/alerts`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ ...newAlert, threshold: Number(newAlert.threshold) }) }); setShowAlertModal(false); setNewAlert({ name:"", question:"", condition:"gt", threshold:"", notifyEmail:"", frequency:"daily" }); loadAlerts(); }}>Create Alert</button>
                    <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowAlertModal(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Scheduled Reports ── */}
        {nav === "schedule" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
              <div><h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Scheduled Reports</h1><p style={{ fontSize: 13, color: T.textSecondary }}>Automatically email reports or AI summaries on a schedule</p></div>
              <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} onClick={() => setShowScheduleModal(true)}>+ New Schedule</button>
            </div>
            {schedules.length === 0 ? (
              <div style={{ textAlign: "center", padding: 64, color: T.textMuted }}><Icon n="clock" size={32} color={T.border} /><p style={{ marginTop: 12 }}>No scheduled reports yet</p></div>
            ) : schedules.map(s => (
              <div key={s.id} className="card" style={{ padding: 16, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <p style={{ fontWeight: 600, marginBottom: 4 }}>{s.reportName}</p>
                  <p style={{ fontSize: 12, color: T.textSecondary }}>{s.question || "Power BI report"}</p>
                  <p style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{s.frequency} · {s.recipients} {s.nextRun ? `· Next: ${new Date(s.nextRun).toLocaleDateString()}` : ""}</p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={async () => { await fetch(`${API_BASE}/scheduled-reports`, { method: "PATCH", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: s.id, isActive: !s.isActive }) }); loadSchedules(); }} style={{ background: s.isActive ? "rgba(34,197,94,.12)" : T.bg, border: `1px solid ${s.isActive ? "rgba(34,197,94,.3)" : T.border}`, borderRadius: 6, padding: "4px 10px", color: s.isActive ? "#22c55e" : T.textMuted, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.isActive ? "Active" : "Paused"}</button>
                  <button onClick={async () => { await fetch(`${API_BASE}/scheduled-reports`, { method: "DELETE", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: s.id }) }); loadSchedules(); }} style={{ background: "none", border: `1px solid rgba(239,68,68,.3)`, borderRadius: 6, padding: "4px 10px", color: "#ef4444", cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Delete</button>
                </div>
              </div>
            ))}
            {showScheduleModal && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                <div className="card" style={{ width: 480, padding: 28 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>New Scheduled Report</h2>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <input className="field-input" placeholder="Report name" value={newSchedule.reportName} onChange={e => setNewSchedule(p => ({ ...p, reportName: e.target.value }))} />
                    <input className="field-input" placeholder="AI question for summary (optional)" value={newSchedule.question} onChange={e => setNewSchedule(p => ({ ...p, question: e.target.value }))} />
                    <input className="field-input" placeholder="Recipients (comma-separated emails)" value={newSchedule.recipients} onChange={e => setNewSchedule(p => ({ ...p, recipients: e.target.value }))} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <select className="field-input" value={newSchedule.frequency} onChange={e => setNewSchedule(p => ({ ...p, frequency: e.target.value }))} style={{ background: T.surface, color: T.textPrimary }}>
                        <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                      </select>
                      <select className="field-input" value={newSchedule.hour} onChange={e => setNewSchedule(p => ({ ...p, hour: Number(e.target.value) }))} style={{ background: T.surface, color: T.textPrimary }}>
                        {[6,7,8,9,10,11,12,13,14,15,16,17,18].map(h => <option key={h} value={h}>{h}:00</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                    <button className="btn-primary" style={{ flex: 1 }} onClick={async () => { await fetch(`${API_BASE}/scheduled-reports`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify(newSchedule) }); setShowScheduleModal(false); setNewSchedule({ reportName:"", question:"", recipients:"", frequency:"weekly", dayOfWeek:1, hour:8 }); loadSchedules(); }}>Create Schedule</button>
                    <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowScheduleModal(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Audit Log ── */}
        {nav === "audit" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Audit Log</h1>
              <p style={{ fontSize: 13, color: T.textSecondary }}>Track who viewed what and when</p>
            </div>
            <div className="card" style={{ padding: 0 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                    {["User","Action","Detail","When"].map(h => <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(l => (
                    <tr key={l.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <td style={{ padding: "10px 16px", fontSize: 13 }}>{l.user?.name || l.user?.email}</td>
                      <td style={{ padding: "10px 16px", fontSize: 12 }}><span style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.2)`, color: T.accent, padding: "2px 8px", borderRadius: 12, fontSize: 11 }}>{l.action}</span></td>
                      <td style={{ padding: "10px 16px", fontSize: 12, color: T.textSecondary, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.detail}</td>
                      <td style={{ padding: "10px 16px", fontSize: 11, color: T.textMuted, whiteSpace: "nowrap" }}>{new Date(l.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                  {auditLogs.length === 0 && <tr><td colSpan={4} style={{ padding: 32, textAlign: "center", color: T.textMuted, fontSize: 13 }}>No activity logged yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

{/* ── Admin Panel ── */}
{nav === "admin" && (
  <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "32px" }}>
    <div style={{ marginBottom: 28 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Admin Panel</h1>
      <p style={{ fontSize: 13, color: T.textSecondary }}>Manage clients, reports and users</p>
    </div>

    {/* Stats */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 28 }}>
      {[
        { label: "Total Clients", value: adminData?._error ? "—" : (adminData?.tenants?.length ?? "…"), icon: "users" },
        { label: "Total Reports", value: adminData?._error ? "—" : (adminData?.reports?.length ?? "…"), icon: "grid" },
        { label: "Platform Status", value: "Live", icon: "zap" },
      ].map(s => (
        <div key={s.label} className="card" style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <Icon n={s.icon} size={16} color={T.accent} />
            <span style={{ fontSize: 12, color: T.textSecondary }}>{s.label}</span>
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: T.textPrimary }}>{s.value}</div>
        </div>
      ))}
    </div>

    {/* Error banner */}
    {adminData?._error && (
      <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 8, padding: "12px 16px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, color: "#f87171" }}>⚠ Failed to load admin data: {adminData._error}</span>
        <button onClick={loadAdminData} style={{ background: "none", border: "1px solid rgba(239,68,68,.4)", borderRadius: 6, padding: "4px 12px", color: "#f87171", cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Retry</button>
      </div>
    )}

{/* Clients list */}
    <div className="card" style={{ padding: 20, marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Clients</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={loadAdminData} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 12px", color: T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>⟳ Refresh</button>
          <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} onClick={() => setShowAddTenant(true)}>
            + Add Client
          </button>
        </div>
      </div>
      {!adminData && <div style={{ color: T.textSecondary, fontSize: 13, padding: "12px 0" }}>Loading…</div>}
      {adminData && !adminData._error && adminData.tenants?.length === 0 && <div style={{ color: T.textSecondary, fontSize: 13, padding: "12px 0" }}>No clients yet. Add one above.</div>}
      {adminData?.tenants?.map(t => (
        <div key={t.id} style={{ borderBottom: `1px solid ${T.border}`, paddingBottom: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0" }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</p>
              <p style={{ fontSize: 12, color: T.textSecondary }}>{t.slug} · {t.dbType === "client" ? "🔗 Client DB" : "🏠 Hosted DB"}</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: t.isActive ? "rgba(34,197,94,.15)" : "rgba(239,68,68,.15)", color: t.isActive ? "#22c55e" : "#ef4444" }}>
                {t.isActive ? "Active" : "Inactive"}
              </span>
              <button onClick={() => setExpandedTenant(expandedTenant === t.id ? null : t.id)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 10px", color: T.textSecondary, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {expandedTenant === t.id ? "Hide" : "Manage"}
              </button>
              <button
                onClick={() => { setViewAs({ id: t.id, name: t.name }); setNav("reports"); setMessages([]); setActiveReport(null); }}
                style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "4px 10px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                👁 View as
              </button>
              <button
                onClick={async () => {
                  const next = !t.isActive;
                  await fetch(`${API_BASE}/admin/tenants/${t.id}`, { method: "PATCH", headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" }, body: JSON.stringify({ isActive: next }) });
                  loadAdminData();
                }}
                style={{ background: "none", border: `1px solid ${t.isActive ? "rgba(239,68,68,.3)" : "rgba(34,197,94,.3)"}`, borderRadius: 6, padding: "4px 10px", color: t.isActive ? "#ef4444" : "#22c55e", cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {t.isActive ? "Disable" : "Enable"}
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm(`Permanently delete "${t.name}" and all their users? This cannot be undone.`)) return;
                  await fetch(`${API_BASE}/admin/tenants/${t.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}` } });
                  loadAdminData();
                }}
                style={{ background: "none", border: "1px solid rgba(239,68,68,.3)", borderRadius: 6, padding: "4px 10px", color: "#ef4444", cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Delete
              </button>
            </div>
          </div>

          {expandedTenant === t.id && (
            <div style={{ background: T.bg, borderRadius: 8, padding: 16, marginTop: 8 }}>

              {/* AI Context Section */}
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>AI Agent — Application & Context</p>

                {/* App type selector */}
                <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 10 }}>
                  <select
                    id={`app-type-${t.id}`}
                    defaultValue={t.appType || "unknown"}
                    className="field-input"
                    style={{ maxWidth: 220, background: T.surface, color: T.textPrimary }}>
                    <option value="unknown">Unknown / Auto-detect</option>
                    <option value="sage100">Sage 100 (MAS 90/200)</option>
                    <option value="sage50">Sage 50</option>
                    <option value="sapb1">SAP Business One</option>
                    <option value="dynamics365">Microsoft Dynamics 365 / BC</option>
                    <option value="quickbooks">QuickBooks</option>
                    <option value="netsuite">NetSuite</option>
                    <option value="odoo">Odoo</option>
                    <option value="syspro">SYSPRO</option>
                    <option value="custom">Custom / Other</option>
                  </select>
                  <button
                    onClick={async (e) => {
                      const btn = e.target;
                      btn.textContent = "Learning…";
                      btn.disabled = true;
                      try {
                        const appType = document.getElementById(`app-type-${t.id}`).value;
                        const res = await fetch(`${API_BASE}/admin/tenants/${t.id}/learn-schema`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                          body: JSON.stringify({ appType })
                        });
                        const data = await res.json();
                        if (!res.ok) { alert("Failed: " + data.error); return; }
                        document.getElementById(`ai-context-${t.id}`).value = data.aiContext;
                        loadAdminData();
                        alert(`✓ Schema learned! Analysed ${data.tableCount} tables. AI Context updated automatically.`);
                      } catch(err) {
                        alert("Error: " + err.message);
                      } finally {
                        btn.textContent = "🧠 Auto-learn schema";
                        btn.disabled = false;
                      }
                    }}
                    style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "6px 14px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap" }}>
                    🧠 Auto-learn schema
                  </button>
                </div>

                <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>Auto-learn connects to the client's database, analyses the schema and generates the context below. You can edit it afterwards.</p>
                <textarea
                  className="field-input"
                  rows={5}
                  placeholder="Click Auto-learn to generate this automatically, or type manually..."
                  defaultValue={t.aiContext || ""}
                  id={`ai-context-${t.id}`}
                  style={{ width: "100%", resize: "vertical", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12 }}
                />
                <button
                  onClick={async () => {
                    const val = document.getElementById(`ai-context-${t.id}`).value;
                    const appType = document.getElementById(`app-type-${t.id}`).value;
                    await fetch(`${API_BASE}/admin/tenants/${t.id}`, {
                      method: "PATCH",
                      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                      body: JSON.stringify({ aiContext: val, appType })
                    });
                    loadAdminData();
                    alert("Saved ✓");
                  }}
                  style={{ marginTop: 8, background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "5px 14px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Save
                </button>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* KPI SQL Editor */}
              {(() => {
                const KPI_LABELS = [
                  { id: 'ytd-rev',      label: 'YTD Revenue',        hint: 'SUM of revenue for current year' },
                  { id: 'mtd-rev',      label: 'MTD Revenue',        hint: 'SUM of revenue for current month' },
                  { id: 'ytd-bud',      label: 'YTD Budget',         hint: 'Budget/target for current year (or SELECT \'No budget data\' AS result)' },
                  { id: 'mtd-bud',      label: 'MTD Budget',         hint: 'Budget/target for current month' },
                  { id: 'ytd-gp',       label: 'YTD GP%',            hint: 'Gross profit % for current year (e.g. 34.9)' },
                  { id: 'mtd-gp',       label: 'MTD GP%',            hint: 'Gross profit % for current month' },
                  { id: 'open-ord',     label: 'Open Orders (count)',  hint: 'COUNT of open confirmed orders' },
                  { id: 'open-ord-val', label: 'Open Order Balance',  hint: 'SUM value of open confirmed orders' },
                  { id: 'open-inv',     label: 'Open Invoices',       hint: 'SUM of outstanding invoice balances' },
                ];
                const kpiSql = t.kpiSql || {};
                return (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>KPI SQL Queries</p>
                      <p style={{ fontSize: 11, color: T.textMuted }}>Write one SELECT query per KPI — must return a single value</p>
                    </div>
                    {KPI_LABELS.map(kpi => (
                      <div key={kpi.id} style={{ marginBottom: 12, background: T.bg, borderRadius: 8, padding: 12, border: `1px solid ${T.border}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <p style={{ fontSize: 11, fontWeight: 700, color: T.accent, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{kpi.label}</p>
                          <button
                            onClick={async () => {
                              const sql = document.getElementById(`kpi-sql-${t.id}-${kpi.id}`).value;
                              if (!sql.trim()) return;
                              const btn = document.getElementById(`kpi-test-${t.id}-${kpi.id}`);
                              btn.textContent = 'Testing…';
                              try {
                                const res = await fetch(`${API_BASE}/admin/tenants/${t.id}/test-sql`, {
                                  method: 'POST',
                                  headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ sql }),
                                });
                                const d = await res.json();
                                if (d.error) { btn.textContent = '✗ Error'; alert('SQL Error: ' + d.error); }
                                else { btn.textContent = '✓ OK: ' + JSON.stringify(Object.values(d.rows[0] || {})[0]); }
                              } catch(e) { btn.textContent = '✗ Failed'; }
                              setTimeout(() => { btn.textContent = 'Test'; }, 4000);
                            }}
                            id={`kpi-test-${t.id}-${kpi.id}`}
                            style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 5, padding: '3px 10px', color: T.textSecondary, cursor: 'pointer', fontSize: 10, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            Test
                          </button>
                        </div>
                        <p style={{ fontSize: 10, color: T.textMuted, marginBottom: 4 }}>{kpi.hint}</p>
                        <textarea
                          className="field-input"
                          id={`kpi-sql-${t.id}-${kpi.id}`}
                          rows={2}
                          defaultValue={kpiSql[kpi.id] || ''}
                          placeholder={`SELECT ... -- ${kpi.hint}`}
                          style={{ width: '100%', fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }}
                        />
                      </div>
                    ))}
                    <button
                      onClick={async () => {
                        const newKpiSql = {};
                        KPI_LABELS.forEach(kpi => {
                          const val = document.getElementById(`kpi-sql-${t.id}-${kpi.id}`).value.trim();
                          if (val) newKpiSql[kpi.id] = val;
                        });
                        const res = await fetch(`${API_BASE}/admin/tenants/${t.id}`, {
                          method: 'PATCH',
                          headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ kpiSql: newKpiSql }),
                        });
                        if (res.ok) { loadAdminData(); alert('KPI SQL saved ✓ — dashboard will use these queries immediately'); }
                        else alert('Save failed');
                      }}
                      style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: '6px 16px', color: T.accent, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      Save All KPI SQL
                    </button>
                  </div>
                );
              })()}

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Power BI Section */}
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Power BI</p>
                <input
                  className="field-input"
                  placeholder="Workspace ID (GUID)"
                  defaultValue={t.pbiWorkspaceId || ""}
                  id={`pbi-ws-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <input
                  className="field-input"
                  placeholder="Master user email (e.g. user@client.com)"
                  defaultValue={t.pbiMasterUser || ""}
                  id={`pbi-user-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <input
                  className="field-input"
                  type="password"
                  placeholder="Master user password"
                  defaultValue={t.pbiMasterPassword || ""}
                  id={`pbi-pass-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <input
                  className="field-input"
                  placeholder="Azure Tenant ID (GUID)"
                  defaultValue={t.azureTenantId || ""}
                  id={`pbi-az-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <button
                  onClick={async () => {
                    const pbiWorkspaceId = document.getElementById(`pbi-ws-${t.id}`).value;
                    const pbiMasterUser = document.getElementById(`pbi-user-${t.id}`).value;
                    const pbiMasterPassword = document.getElementById(`pbi-pass-${t.id}`).value;
                    const azureTenantId = document.getElementById(`pbi-az-${t.id}`).value;
                    const res = await fetch(`${API_BASE}/admin/tenants/${t.id}`, {
                      method: "PATCH",
                      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                      body: JSON.stringify({ pbiWorkspaceId, pbiMasterUser, pbiMasterPassword, azureTenantId }),
                    });
                    if (res.ok) { loadAdminData(); alert("Saved ✓"); }
                    else alert("Save failed");
                  }}
                  style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "5px 14px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Save Power BI
                </button>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Database Connection Section */}
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Database Connection</p>
                <input
                  className="field-input"
                  placeholder="Connection string (postgresql://...)"
                  defaultValue={t.dbConnectionString || ""}
                  id={`db-conn-${t.id}`}
                  style={{ width: "100%", marginBottom: 8, fontFamily: "monospace", fontSize: 11 }}
                />
                <input
                  className="field-input"
                  placeholder="Schema (e.g. raindrop_snapshot)"
                  defaultValue={t.dbSchema || ""}
                  id={`db-schema-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <button
                  onClick={async () => {
                    const dbConnectionString = document.getElementById(`db-conn-${t.id}`).value;
                    const dbSchema = document.getElementById(`db-schema-${t.id}`).value;
                    const res = await fetch(`${API_BASE}/admin/tenants/${t.id}`, {
                      method: "PATCH",
                      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                      body: JSON.stringify({ dbConnectionString, dbSchema }),
                    });
                    if (res.ok) { loadAdminData(); alert("Saved ✓"); }
                    else alert("Save failed");
                  }}
                  style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "5px 14px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Save Connection
                </button>
                <TestConnButton connId={`db-conn-${t.id}`} schemaId={`db-schema-${t.id}`} />
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Data Sync Section */}
              <div style={{ marginBottom: 20 }}>
                <SyncStatusSection tenantId={t.id} />
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Reports Section */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>Reports</p>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {adminData?.reports?.map(r => {
                    const assigned = t.reports?.some(tr => tr.report?.id === r.id || tr.reportId === r.id)
                    return (
                      <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 6, background: assigned ? "rgba(196,122,16,.08)" : "rgba(255,255,255,.03)", border: `1px solid ${assigned ? T.accent : T.border}` }}>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 500 }}>{r.name}</p>
                          <p style={{ fontSize: 11, color: T.textSecondary }}>{r.description || "No description"}</p>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API_BASE}/admin/tenants/${t.id}/reports`, {
                                method: assigned ? "DELETE" : "POST",
                                headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                                body: JSON.stringify({ reportId: r.id }),
                              })
                              if (res.ok) loadAdminData()
                              else alert("Failed to update report assignment")
                            } catch (err) {
                              alert("Error: " + err.message)
                            }
                          }}
                          style={{ background: assigned ? "rgba(239,68,68,.15)" : "rgba(196,122,16,.15)", border: "none", borderRadius: 6, padding: "4px 12px", color: assigned ? "#ef4444" : T.accent, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                        >
                          {assigned ? "Remove" : "Assign"}
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Users Section */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>Users</p>
                <button onClick={() => { setShowAddUser(true); setAddUserTenantId(t.id); setAddUserTenantName(t.name); setNewUser({ name: "", email: "", role: "VIEWER" }); }} style={{ background: "none", border: `1px solid ${T.accent}`, borderRadius: 6, padding: "4px 10px", color: T.accent, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  + Invite User
                </button>
              </div>
              {!t.users || t.users.length === 0 ? (
                <p style={{ fontSize: 12, color: T.textMuted }}>No users yet — invite your first user above.</p>
              ) : (
                t.users.map(u => (
                  <div key={u.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 500 }}>{u.name}</p>
                      <p style={{ fontSize: 11, color: T.textSecondary }}>{u.email}</p>
                    </div>
                    <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "rgba(196,122,16,.15)", color: T.accent }}>{u.role}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      ))}
    </div>

    {/* Invite user modal — top level so it's never trapped by overflow/transform */}
    {showAddUser && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
        <div className="card" style={{ width: 420, padding: 28 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Invite User</h2>
          <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 20 }}>Adding to: {addUserTenantName}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input className="field-input" placeholder="Full name" value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} />
            <input className="field-input" placeholder="Email address" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} />
            <select className="field-input" value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))} style={{ background: T.surface, color: T.textPrimary }}>
              <option value="VIEWER">Viewer</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button className="btn-primary" style={{ flex: 1 }} onClick={inviteUser} disabled={inviteLoading}>
              {inviteLoading ? "Sending invite..." : "Send Invitation"}
            </button>
            <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowAddUser(false)}>Cancel</button>
          </div>
        </div>
      </div>
    )}

    {/* Add tenant modal */}
    {showAddTenant && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
        <div className="card" style={{ width: 440, padding: 28 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>Add New Client</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input className="field-input" placeholder="Company name (e.g. Acme Corp)" value={newTenant.name} onChange={e => setNewTenant(p => ({ ...p, name: e.target.value }))} />
            <input className="field-input" placeholder="Slug (e.g. acme-corp)" value={newTenant.slug} onChange={e => setNewTenant(p => ({ ...p, slug: e.target.value }))} />
            <div>
              <input className="field-input" placeholder="Azure Tenant ID (from Azure Portal → Overview)" value={newTenant.azureTenantId} onChange={e => setNewTenant(p => ({ ...p, azureTenantId: e.target.value }))} />
              <p style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Azure Portal → Azure Active Directory → Overview → Tenant ID</p>
            </div>
            <input className="field-input" placeholder="Power BI Workspace ID (optional)" value={newTenant.pbiWorkspaceId} onChange={e => setNewTenant(p => ({ ...p, pbiWorkspaceId: e.target.value }))} />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setNewTenant(p => ({ ...p, dbType: "hosted" }))} style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid ${newTenant.dbType === "hosted" ? T.accent : T.border}`, background: newTenant.dbType === "hosted" ? T.accentGlow2 : "transparent", color: newTenant.dbType === "hosted" ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                🏠 We host (our Supabase)
              </button>
              <button onClick={() => setNewTenant(p => ({ ...p, dbType: "client" }))} style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid ${newTenant.dbType === "client" ? T.accent : T.border}`, background: newTenant.dbType === "client" ? T.accentGlow2 : "transparent", color: newTenant.dbType === "client" ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                🔗 Client's own DB
              </button>
            </div>
            {newTenant.dbType === "client" && (
              <>
                <input className="field-input" placeholder="Connection string (postgresql://...)" value={newTenant.dbConnectionString} onChange={e => setNewTenant(p => ({ ...p, dbConnectionString: e.target.value }))} />
                <input className="field-input" placeholder="Schema name (e.g. public)" value={newTenant.dbSchema} onChange={e => setNewTenant(p => ({ ...p, dbSchema: e.target.value }))} />
                <TestConnButton connString={newTenant.dbConnectionString} schema={newTenant.dbSchema} />
              </>
            )}
            {newTenant.dbType === "hosted" && (
              <input className="field-input" placeholder="Schema name (e.g. acme_corp)" value={newTenant.dbSchema} onChange={e => setNewTenant(p => ({ ...p, dbSchema: e.target.value }))} />
            )}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button className="btn-primary" style={{ flex: 1 }} onClick={addTenant}>Create Client</button>
            <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowAddTenant(false)}>Cancel</button>
          </div>
        </div>
      </div>
    )}

  </div>
)}
        {/* ── Settings ── */}
        {nav === "settings" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div className="fu" style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Settings</h1>
              <p style={{ fontSize: 13, color: T.textSecondary }}>Manage your account preferences</p>
            </div>
            <div className="card fu1" style={{ maxWidth: 520, marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Account</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[["Display name", user.name], ["Email", user.email], ["Tenant", tenantName]].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                    <span style={{ fontSize: 13, color: T.textSecondary }}>{k}</span>
                    <span style={{ fontSize: 13, color: T.textPrimary }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card fu2" style={{ maxWidth: 520 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Session</h3>
              <button className="btn-secondary" style={{ color: T.error, borderColor: "rgba(248,81,73,.3)" }} onClick={onLogout}>Sign out</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Admin: Test connection button ───────────────────────────────────────────
// Works both with uncontrolled inputs (pass connId/schemaId element IDs) and
// controlled values (pass connString/schema). Gives instant onboarding feedback.
function TestConnButton({ connId, schemaId, connString, schema }) {
  const [status, setStatus] = useState(null); // { ok, msg }
  const [loading, setLoading] = useState(false);
  const run = async () => {
    const dbConnectionString = (connId ? document.getElementById(connId)?.value : connString || "").trim();
    const dbSchema = (schemaId ? document.getElementById(schemaId)?.value : schema || "").trim();
    if (!dbConnectionString) { setStatus({ ok: false, msg: "Enter a connection string first" }); return; }
    setLoading(true); setStatus(null);
    try {
      const res = await fetch(`${API_BASE}/admin/tenants/test-connection`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
        body: JSON.stringify({ dbConnectionString, dbSchema }),
      });
      const d = await res.json();
      if (d.ok) setStatus({ ok: true, msg: `Connected — ${d.tableCount} tables, ${Number(d.totalRows).toLocaleString()} rows in ${d.schemaChecked}` });
      else setStatus({ ok: false, msg: d.error || "Connection failed" });
    } catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally { setLoading(false); }
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
      <button onClick={run} disabled={loading}
        style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 14px", color: T.textSecondary, cursor: loading ? "default" : "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {loading ? "Testing…" : "⚡ Test connection"}
      </button>
      {status && <span style={{ fontSize: 11, color: status.ok ? "#22c55e" : "#ef4444" }}>{status.ok ? "✓ " : "✗ "}{status.msg}</span>}
    </div>
  );
}

// ─── Admin: Data Sync freshness panel ─────────────────────────────────────────
function SyncStatusSection({ tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/sync-status`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("pvbi_token")}` },
      });
      const d = await res.json();
      if (d.ok) setData(d); else setErr(d.error || "Failed to read sync status");
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };
  const fmtAge = (h) => h == null ? "—" : h < 1 ? `${Math.round(h * 60)} min ago` : h < 48 ? `${h.toFixed(1)} h ago` : `${Math.round(h / 24)} d ago`;
  const fmtTs = (ts) => ts ? new Date(ts).toLocaleString() : "—";
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>Data Sync</p>
        <button onClick={load} disabled={loading}
          style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 12px", color: T.textSecondary, cursor: loading ? "default" : "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {loading ? "Checking…" : "↻ Check status"}
        </button>
      </div>
      {err && <p style={{ fontSize: 11, color: "#ef4444" }}>✗ {err}</p>}
      {data && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
            {data.hasWatermarks ? (
              <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: data.stale ? "rgba(239,68,68,.15)" : "rgba(34,197,94,.15)", color: data.stale ? "#ef4444" : "#22c55e", fontWeight: 600 }}>
                {data.stale ? "⚠ Stale" : "✓ Fresh"} · data as of {fmtAge(data.ageHours)}
              </span>
            ) : (
              <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "rgba(148,163,184,.15)", color: T.textMuted }}>
                No sync watermark (not a ClientSync tenant)
              </span>
            )}
            <span style={{ fontSize: 11, color: T.textMuted }}>{data.tableCount} tables · {Number(data.totalRows).toLocaleString()} rows</span>
          </div>
          <div style={{ maxHeight: 220, overflow: "auto", border: `1px solid ${T.border}`, borderRadius: 6 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ color: T.textMuted, textAlign: "left" }}>
                  <th style={{ padding: "6px 10px", fontWeight: 500 }}>Table</th>
                  <th style={{ padding: "6px 10px", fontWeight: 500, textAlign: "right" }}>Rows</th>
                  <th style={{ padding: "6px 10px", fontWeight: 500 }}>Watermark</th>
                  <th style={{ padding: "6px 10px", fontWeight: 500 }}>Last sync</th>
                </tr>
              </thead>
              <tbody>
                {data.tables.map((t) => (
                  <tr key={t.name} style={{ borderTop: `1px solid ${T.border}` }}>
                    <td style={{ padding: "6px 10px", color: T.textPrimary, fontFamily: "monospace" }}>{t.name}</td>
                    <td style={{ padding: "6px 10px", color: T.textSecondary, textAlign: "right" }}>{Number(t.rows).toLocaleString()}</td>
                    <td style={{ padding: "6px 10px", color: t.lastValue ? T.textSecondary : T.textMuted }}>{t.lastValue || "—"}</td>
                    <td style={{ padding: "6px 10px", color: T.textMuted }}>{fmtTs(t.updatedAt)}</td>
                  </tr>
                ))}
                {data.tables.length === 0 && <tr><td colSpan={4} style={{ padding: 16, textAlign: "center", color: T.textMuted }}>No tables in schema “{data.schema}”.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── CRM ──────────────────────────────────────────────────────────────────────
const CRM_STAGES = [
  { id: "lead",      label: "Lead",      color: "#8b949e" },
  { id: "qualified", label: "Qualified", color: "#3b82f6" },
  { id: "demo",      label: "Demo",      color: "#a78bfa" },
  { id: "proposal",  label: "Proposal",  color: "#f59e0b" },
  { id: "won",       label: "Won",       color: "#2ea043" },
  { id: "lost",      label: "Lost",      color: "#ef4444" },
];
const CRM_OPEN_STAGES = CRM_STAGES.filter(s => s.id !== "won" && s.id !== "lost");

function crmApi(path, opts = {}) {
  const imp = sessionStorage.getItem("pvbi_impersonate");
  return fetch(`${API_BASE}/crm${path}`, {
    ...opts,
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem("pvbi_token")}`,
      "Content-Type": "application/json",
      ...(imp ? { "X-Impersonate-Tenant": imp } : {}),
      ...(opts.headers || {}),
    },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || ("HTTP " + r.status)); return d; });
}
const crmMoney = v => { v = Number(v) || 0; return v >= 1e6 ? "$" + (v / 1e6).toFixed(1) + "M" : v >= 1e3 ? "$" + (v / 1e3).toFixed(0) + "K" : "$" + Math.round(v); };
const crmDate = d => d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

// Loggable activity types (the model also has system/stage_change which are auto-generated)
const CRM_ACT_TYPES = [
  { id: "note",    label: "Note",    icon: "📝" },
  { id: "call",    label: "Call",    icon: "📞" },
  { id: "email",   label: "Email",   icon: "✉️" },
  { id: "meeting", label: "Meeting", icon: "🤝" },
];
const crmActMeta = t => CRM_ACT_TYPES.find(x => x.id === t)
  || (t === "stage_change" ? { icon: "🔀", label: "Stage", color: "#a78bfa" }
    : t === "system" ? { icon: "⚙️", label: "System", color: "#8b949e" }
    : { icon: "•", label: t, color: "#8b949e" });
const CRM_PRIORITIES = [
  { id: "high",   label: "High",   color: "#ef4444" },
  { id: "normal", label: "Normal", color: "#8b949e" },
  { id: "low",    label: "Low",    color: "#3b82f6" },
];

// --- tiny CSV helpers (no dependency) ---
function crmToCsv(rows, cols) {
  const esc = v => { v = v == null ? "" : String(v); return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v; };
  return [cols.map(c => esc(c.label)).join(","), ...rows.map(r => cols.map(c => esc(c.get(r))).join(","))].join("\n");
}
function crmDownload(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function crmParseCsv(text) {
  const rows = []; let i = 0, field = "", row = [], inQ = false;
  const pushF = () => { row.push(field); field = ""; };
  const pushR = () => { pushF(); rows.push(row); row = []; };
  while (i < text.length) {
    const c = text[i];
    if (inQ) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
      else field += c;
    } else if (c === '"') inQ = true;
    else if (c === ",") pushF();
    else if (c === "\n") pushR();
    else if (c !== "\r") field += c;
    i++;
  }
  if (field.length || row.length) pushR();
  if (!rows.length) return [];
  const headers = rows[0].map(h => h.trim());
  return rows.slice(1)
    .filter(r => r.some(c => c.trim() !== ""))
    .map(r => Object.fromEntries(headers.map((h, idx) => [h, (r[idx] || "").trim()])));
}

function CrmApp() {
  const [tab, setTab] = useState("pipeline");
  const [opps, setOpps] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [leads, setLeads] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [err, setErr] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [detailAcct, setDetailAcct] = useState(null);
  const [detailOpp, setDetailOpp] = useState(null);
  const [showNewOpp, setShowNewOpp] = useState(false);
  const [showNewAcct, setShowNewAcct] = useState(false);
  const [showNewLead, setShowNewLead] = useState(false);
  const [convertLead, setConvertLead] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const loadBoard = () => crmApi("/opportunities").then(d => setOpps(d.opportunities)).catch(e => setErr(e.message));
  const loadAccounts = () => crmApi("/accounts").then(d => setAccounts(d.accounts)).catch(e => setErr(e.message));
  const loadTasks = () => crmApi("/tasks").then(d => setTasks(d.tasks)).catch(e => setErr(e.message));
  const loadLeads = () => crmApi("/leads").then(d => setLeads(d.leads)).catch(e => setErr(e.message));
  const loadAnalytics = () => crmApi("/analytics").then(setAnalytics).catch(e => setErr(e.message));
  const refreshAll = () => { loadBoard(); loadAccounts(); loadAnalytics(); if (tab === "tasks") loadTasks(); if (tab === "leads") loadLeads(); };

  useEffect(() => { loadBoard(); loadAccounts(); loadAnalytics(); }, []);
  useEffect(() => { if (tab === "tasks" && !tasks) loadTasks(); if (tab === "leads" && !leads) loadLeads(); }, [tab]); // eslint-disable-line

  const moveOpp = async (id, stage) => {
    setOpps(prev => prev.map(o => o.id === id ? { ...o, stage } : o));
    try { await crmApi(`/opportunities/${id}`, { method: "PATCH", body: JSON.stringify({ stage }) }); loadAnalytics(); }
    catch (e) { setErr(e.message); loadBoard(); }
  };

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 };
  const a = analytics?.summary;

  const exportCurrent = () => {
    if (tab === "accounts") {
      crmDownload("crm-accounts.csv", crmToCsv(accounts || [], [
        { label: "name", get: x => x.name }, { label: "status", get: x => x.status },
        { label: "industry", get: x => x.industry || "" }, { label: "website", get: x => x.website || "" },
        { label: "openPipeline", get: x => x.openPipeline || 0 },
        { label: "opportunities", get: x => x.counts?.opportunities ?? 0 },
        { label: "contacts", get: x => x.counts?.contacts ?? 0 },
      ]));
    } else {
      crmDownload("crm-pipeline.csv", crmToCsv(opps || [], [
        { label: "opportunity", get: o => o.name }, { label: "account", get: o => o.account?.name || "" },
        { label: "stage", get: o => o.stage }, { label: "amount", get: o => o.amount || 0 },
        { label: "probability", get: o => o.probability ?? "" },
        { label: "expectedClose", get: o => o.expectedCloseDate ? new Date(o.expectedCloseDate).toISOString().slice(0, 10) : "" },
      ]));
    }
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "28px 32px", overflowY: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>CRM</h1>
          <p style={{ fontSize: 13, color: T.textSecondary }}>Opportunities, tasks, onboarding & pipeline analytics</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <CrmSearch onOpenAcct={setDetailAcct} onOpenOpp={setDetailOpp} />
          <button className="btn-secondary" style={{ width: "auto", padding: "8px 14px", fontSize: 12 }} onClick={exportCurrent} title={tab === "accounts" ? "Export accounts to CSV" : "Export pipeline to CSV"}>↓ Export</button>
          <button className="btn-secondary" style={{ width: "auto", padding: "8px 14px", fontSize: 12 }} onClick={() => setShowImport(true)}>↑ Import</button>
          <button className="btn-secondary" style={{ width: "auto", padding: "8px 12px", fontSize: 12 }} onClick={() => setShowSettings(true)} title="Products, custom fields & automation">⚙</button>
          <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} onClick={() => setShowNewLead(true)}>+ Lead</button>
          <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} onClick={() => setShowNewAcct(true)}>+ Account</button>
          <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} onClick={() => setShowNewOpp(true)}>+ Opportunity</button>
        </div>
      </div>

      {err && <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#f87171" }}>⚠ {err} <button onClick={() => { setErr(null); refreshAll(); }} style={{ marginLeft: 8, background: "none", border: "none", color: "#f87171", cursor: "pointer", textDecoration: "underline" }}>retry</button></div>}

      {/* KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Open Pipeline", value: a ? crmMoney(a.openValue) : "…", sub: a ? `${a.openCount} deals` : "" },
          { label: "Weighted Forecast", value: a ? crmMoney(a.weightedForecast) : "…", sub: "probability-adjusted" },
          { label: "Win Rate", value: a?.winRate != null ? a.winRate + "%" : "—", sub: a ? `${a.wonCount}W / ${a.lostCount}L` : "" },
          { label: "Avg Deal", value: a ? crmMoney(a.avgDealSize) : "…", sub: a?.avgCycleDays != null ? `${a.avgCycleDays}d cycle` : "" },
          { label: "Overdue Tasks", value: a != null ? a.tasksOverdue : "…", sub: a ? `${a.tasksOpen} open` : "", warn: a?.tasksOverdue > 0 },
        ].map(k => (
          <div key={k.label} style={card}>
            <p style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>{k.label}</p>
            <p style={{ fontSize: 22, fontWeight: 700, color: k.warn ? "#ef4444" : T.textPrimary }}>{k.value}</p>
            <p style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{k.sub}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 4, width: "fit-content" }}>
        {[["leads", "Leads"], ["pipeline", "Pipeline"], ["accounts", "Accounts"], ["tasks", "Tasks"], ["activity", "Activity"], ["analytics", "Analytics"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ padding: "7px 18px", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", background: tab === id ? T.bg : "transparent", color: tab === id ? T.accent : T.textSecondary }}>{label}</button>
        ))}
      </div>

      {/* ── Leads ── */}
      {tab === "leads" && <CrmLeads leads={leads} reload={loadLeads} onConvert={setConvertLead} onOpenAcct={setDetailAcct} />}

      {/* ── Pipeline kanban ── */}
      {tab === "pipeline" && (
        <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8 }}>
          {CRM_STAGES.map(stage => {
            const items = (opps || []).filter(o => o.stage === stage.id);
            const total = items.reduce((s, o) => s + (o.amount || 0), 0);
            return (
              <div key={stage.id}
                onDragOver={e => e.preventDefault()}
                onDrop={() => { if (dragId) moveOpp(dragId, stage.id); setDragId(null); }}
                style={{ minWidth: 240, width: 240, flexShrink: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 10, display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px 10px" }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: stage.color }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary }}>{stage.label}</span>
                  <span style={{ fontSize: 11, color: T.textMuted, marginLeft: "auto" }}>{items.length} · {crmMoney(total)}</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 40 }}>
                  {opps === null && <p style={{ fontSize: 12, color: T.textMuted, padding: 8 }}>Loading…</p>}
                  {items.map(o => (
                    <div key={o.id} draggable
                      onDragStart={() => setDragId(o.id)} onDragEnd={() => setDragId(null)}
                      onClick={() => setDetailOpp(o.id)}
                      style={{ background: T.bg, border: `1px solid ${T.border}`, borderLeft: `3px solid ${stage.color}`, borderRadius: 8, padding: "10px 12px", cursor: "pointer" }}>
                      <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{o.name}</p>
                      <p style={{ fontSize: 11, color: T.textSecondary, marginBottom: 6 }}>{o.account?.name}</p>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: T.accent, fontFamily: "'JetBrains Mono',monospace" }}>{crmMoney(o.amount)}</span>
                        <span style={{ fontSize: 10, color: T.textMuted }}>{o.probability}%{o._count?.tasks ? ` · ${o._count.tasks}✓` : ""}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Accounts ── */}
      {tab === "accounts" && (
        <div style={card}>
          {accounts === null ? <p style={{ color: T.textMuted, fontSize: 13 }}>Loading…</p> :
            accounts.length === 0 ? <p style={{ color: T.textMuted, fontSize: 13 }}>No accounts yet. Add your first with “+ Account”.</p> : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ color: T.textMuted, textAlign: "left" }}>
                {["Account", "Status", "Open Pipeline", "Deals", "Contacts", ""].map(h => <th key={h} style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, textTransform: "uppercase" }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {accounts.map(ac => (
                  <tr key={ac.id} style={{ borderTop: `1px solid ${T.border}`, cursor: "pointer" }} onClick={() => setDetailAcct(ac.id)}>
                    <td style={{ padding: "10px 12px", fontWeight: 600 }}>{ac.name}{ac.industry ? <span style={{ color: T.textMuted, fontWeight: 400 }}> · {ac.industry}</span> : ""}</td>
                    <td style={{ padding: "10px 12px" }}><span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: ac.status === "active" ? "rgba(46,160,67,.15)" : ac.status === "churned" ? "rgba(239,68,68,.15)" : "rgba(148,163,184,.15)", color: ac.status === "active" ? "#2ea043" : ac.status === "churned" ? "#ef4444" : T.textMuted }}>{ac.status}</span></td>
                    <td style={{ padding: "10px 12px", color: T.accent, fontFamily: "'JetBrains Mono',monospace" }}>{crmMoney(ac.openPipeline)}</td>
                    <td style={{ padding: "10px 12px", color: T.textSecondary }}>{ac.counts.opportunities}</td>
                    <td style={{ padding: "10px 12px", color: T.textSecondary }}>{ac.counts.contacts}</td>
                    <td style={{ padding: "10px 12px", color: T.textMuted, textAlign: "right" }}>›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Tasks ── */}
      {tab === "tasks" && <CrmTasks tasks={tasks} reload={() => { loadTasks(); loadAnalytics(); }} accounts={accounts} opps={opps} />}

      {/* ── Activity feed ── */}
      {tab === "activity" && <CrmActivityFeed onOpenAcct={setDetailAcct} onOpenOpp={setDetailOpp} />}

      {/* ── Analytics ── */}
      {tab === "analytics" && <CrmAnalytics analytics={analytics} />}

      {showImport && <CrmImportModal onClose={() => setShowImport(false)} onDone={() => { loadAccounts(); loadAnalytics(); }} />}
      {detailAcct && <CrmAccountModal accountId={detailAcct} onClose={() => { setDetailAcct(null); refreshAll(); }} />}
      {detailOpp && <CrmOppModal oppId={detailOpp} onClose={() => { setDetailOpp(null); refreshAll(); }} />}
      {showNewOpp && <CrmNewOpp accounts={accounts} onClose={() => setShowNewOpp(false)} onSaved={() => { setShowNewOpp(false); refreshAll(); }} onNeedAccount={() => { setShowNewOpp(false); setShowNewAcct(true); }} />}
      {showNewAcct && <CrmNewAccount onClose={() => setShowNewAcct(false)} onSaved={() => { setShowNewAcct(false); loadAccounts(); loadAnalytics(); }} />}
      {showSettings && <CrmSettings onClose={() => setShowSettings(false)} />}
      {showNewLead && <CrmNewLead onClose={() => setShowNewLead(false)} onSaved={() => { setShowNewLead(false); setTab("leads"); loadLeads(); }} />}
      {convertLead && <CrmConvertLead lead={convertLead} accounts={accounts} onClose={() => setConvertLead(null)} onDone={(r) => { setConvertLead(null); loadLeads(); refreshAll(); if (r?.opportunityId) setDetailOpp(r.opportunityId); else if (r?.accountId) setDetailAcct(r.accountId); }} />}
    </div>
  );
}

const crmModalWrap = { position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 };
const crmInput = { width: "100%", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "9px 12px", color: T.textPrimary, fontSize: 13, fontFamily: "'Plus Jakarta Sans',sans-serif", marginBottom: 10 };

// ── Global search across accounts / contacts / opportunities ──
function CrmSearch({ onOpenAcct, onOpenOpp }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const id = setTimeout(() => {
      crmApi(`/search?q=${encodeURIComponent(q.trim())}`).then(d => { setRes(d); setOpen(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(id);
  }, [q]);
  const total = res ? res.accounts.length + res.contacts.length + res.opportunities.length : 0;
  const pick = (fn) => { fn(); setOpen(false); setQ(""); setRes(null); };
  const itemStyle = { padding: "8px 12px", cursor: "pointer", borderTop: `1px solid ${T.border}`, fontSize: 13 };
  return (
    <div style={{ position: "relative" }}>
      <input
        value={q} onChange={e => setQ(e.target.value)}
        onFocus={() => res && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search CRM…"
        style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", color: T.textPrimary, fontSize: 13, width: 220, fontFamily: "'Plus Jakarta Sans',sans-serif" }}
      />
      {open && q.trim().length >= 2 && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, width: 320, maxHeight: 380, overflowY: "auto", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, zIndex: 1100, boxShadow: "0 10px 30px rgba(0,0,0,.4)" }}>
          {total === 0 && <p style={{ padding: 14, fontSize: 13, color: T.textMuted }}>No matches</p>}
          {res?.accounts.length > 0 && <p style={{ padding: "8px 12px 4px", fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>Accounts</p>}
          {res?.accounts.map(ac => (
            <div key={ac.id} style={itemStyle} onMouseDown={() => pick(() => onOpenAcct(ac.id))}>
              <span style={{ fontWeight: 600 }}>{ac.name}</span>{ac.industry ? <span style={{ color: T.textMuted }}> · {ac.industry}</span> : ""}
            </div>
          ))}
          {res?.opportunities.length > 0 && <p style={{ padding: "8px 12px 4px", fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>Opportunities</p>}
          {res?.opportunities.map(o => (
            <div key={o.id} style={itemStyle} onMouseDown={() => pick(() => onOpenOpp(o.id))}>
              <span style={{ fontWeight: 600 }}>{o.name}</span> <span style={{ color: T.accent }}>{crmMoney(o.amount)}</span>
              <div style={{ fontSize: 11, color: T.textMuted }}>{o.account?.name}</div>
            </div>
          ))}
          {res?.contacts.length > 0 && <p style={{ padding: "8px 12px 4px", fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>Contacts</p>}
          {res?.contacts.map(c => (
            <div key={c.id} style={itemStyle} onMouseDown={() => c.account && pick(() => onOpenAcct(c.account.id))}>
              <span style={{ fontWeight: 600 }}>{c.name}</span>{c.role ? <span style={{ color: T.textMuted }}> · {c.role}</span> : ""}
              <div style={{ fontSize: 11, color: T.textMuted }}>{c.account?.name}{c.email ? ` · ${c.email}` : ""}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Unified recent-activity feed ──
function CrmActivityFeed({ onOpenAcct, onOpenOpp }) {
  const [acts, setActs] = useState(null);
  useEffect(() => { crmApi("/activities?limit=100").then(d => setActs(d.activities)).catch(() => setActs([])); }, []);
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20 };
  if (acts === null) return <div style={card}><p style={{ color: T.textMuted, fontSize: 13 }}>Loading…</p></div>;
  if (acts.length === 0) return <div style={card}><p style={{ color: T.textMuted, fontSize: 13 }}>No activity yet. Log calls, emails and notes from any account or opportunity.</p></div>;
  // group by day
  const groups = {};
  for (const a of acts) { const k = new Date(a.createdAt).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" }); (groups[k] ||= []).push(a); }
  return (
    <div style={{ ...card, display: "flex", flexDirection: "column", gap: 18 }}>
      {Object.entries(groups).map(([day, items]) => (
        <div key={day}>
          <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10 }}>{day}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map(ac => { const m = crmActMeta(ac.type); return (
              <div key={ac.id} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ fontSize: 15, width: 22, textAlign: "center", flexShrink: 0 }}>{m.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, color: T.textPrimary }}>{ac.body}</p>
                  <p style={{ fontSize: 11, color: T.textMuted }}>
                    {ac.authorName ? `${ac.authorName} · ` : ""}{new Date(ac.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    {ac.opportunity ? <> · <span style={{ color: T.accent, cursor: "pointer" }} onClick={() => onOpenOpp(ac.opportunity.id)}>{ac.opportunity.name}</span></>
                      : ac.account ? <> · <span style={{ color: T.accent, cursor: "pointer" }} onClick={() => onOpenAcct(ac.account.id)}>{ac.account.name}</span></> : null}
                  </p>
                </div>
              </div>
            ); })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── CSV import (accounts or contacts) ──
function CrmImportModal({ onClose, onDone }) {
  const [type, setType] = useState("accounts");
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const rows = text.trim() ? crmParseCsv(text) : [];
  const onFile = (e) => { const f = e.target.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = () => setText(String(r.result || "")); r.readAsText(f); };
  const run = async () => {
    if (!rows.length) return;
    setBusy(true); setResult(null);
    try { const d = await crmApi("/import", { method: "POST", body: JSON.stringify({ type, rows }) }); setResult(d); onDone(); }
    catch (e) { alert(e.message); }
    setBusy(false);
  };
  const headers = type === "accounts"
    ? "name, website, industry, status, contactName, contactEmail, contactRole"
    : "name, email, phone, role, account";
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 560, maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", padding: 24 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600 }}>Import from CSV</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {[["accounts", "Accounts"], ["contacts", "Contacts"]].map(([id, label]) => (
            <button key={id} onClick={() => { setType(id); setResult(null); }} style={{ padding: "6px 16px", borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", border: `1px solid ${type === id ? T.accent : T.border}`, background: type === id ? T.accent + "22" : "transparent", color: type === id ? T.accent : T.textSecondary }}>{label}</button>
          ))}
        </div>
        <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 6 }}>First row must be a header. Recognised columns:</p>
        <p style={{ fontSize: 12, color: T.textSecondary, fontFamily: "'JetBrains Mono',monospace", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: "8px 10px", marginBottom: 12 }}>{headers}</p>
        {type === "contacts" && <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 12 }}>The <strong>account</strong> column must match an existing account name exactly.</p>}
        <input type="file" accept=".csv,text/csv" onChange={onFile} style={{ fontSize: 12, color: T.textSecondary, marginBottom: 10 }} />
        <textarea value={text} onChange={e => { setText(e.target.value); setResult(null); }} placeholder="…or paste CSV here" rows={7} style={{ ...crmInput, fontFamily: "'JetBrains Mono',monospace", resize: "vertical" }} />
        {rows.length > 0 && !result && <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 12 }}>{rows.length} row{rows.length === 1 ? "" : "s"} ready to import.</p>}
        {result && (
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13 }}>
            <p style={{ color: "#2ea043", fontWeight: 600 }}>✓ Imported {result.created}{result.skipped ? ` · skipped ${result.skipped}` : ""}</p>
            {result.errors?.length > 0 && <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: T.textMuted, fontSize: 12 }}>{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-primary" style={{ flex: 1 }} disabled={busy || !rows.length} onClick={run}>{busy ? "Importing…" : `Import ${rows.length || ""}`.trim()}</button>
          <button className="btn-secondary" style={{ flex: 1 }} onClick={onClose}>{result ? "Done" : "Cancel"}</button>
        </div>
      </div>
    </div>
  );
}

function CrmTasks({ tasks, reload, accounts, opps }) {
  const blank = { title: "", dueDate: "", type: "task", priority: "normal", accountId: "", opportunityId: "" };
  const [f, setF] = useState(blank);
  const [more, setMore] = useState(false);
  const add = async () => {
    if (!f.title.trim()) return;
    await crmApi("/tasks", { method: "POST", body: JSON.stringify({
      title: f.title, dueDate: f.dueDate || null, type: f.type, priority: f.priority,
      accountId: f.accountId || null, opportunityId: f.opportunityId || null,
    }) });
    setF(blank); reload();
  };
  const toggle = async (t) => { await crmApi(`/tasks/${t.id}`, { method: "PATCH", body: JSON.stringify({ status: t.status === "done" ? "open" : "done" }) }); reload(); };
  const del = async (t) => { await crmApi(`/tasks/${t.id}`, { method: "DELETE" }); reload(); };
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 };
  const now = new Date(); const todayEnd = new Date(now); todayEnd.setHours(23, 59, 59, 999);
  const open = (tasks || []).filter(t => t.status === "open");
  const done = (tasks || []).filter(t => t.status === "done");
  const bucket = (t) => {
    if (!t.dueDate) return "nodate";
    const d = new Date(t.dueDate).getTime();
    if (d < now.getTime()) return "overdue";
    if (d <= todayEnd.getTime()) return "today";
    return "upcoming";
  };
  const TYPE_ICON = { call: "📞", email: "✉️", meeting: "🤝", setup: "🔧" };
  const row = (t) => {
    const b = bucket(t);
    const pr = CRM_PRIORITIES.find(p => p.id === t.priority);
    const overdue = b === "overdue" && t.status === "open";
    return (
      <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: `1px solid ${T.border}` }}>
        <input type="checkbox" checked={t.status === "done"} onChange={() => toggle(t)} style={{ width: 16, height: 16, cursor: "pointer", accentColor: T.accent }} />
        {t.priority && t.priority !== "normal" && t.status === "open" && <span title={pr?.label + " priority"} style={{ width: 7, height: 7, borderRadius: "50%", background: pr?.color, flexShrink: 0 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 13, fontWeight: 500, textDecoration: t.status === "done" ? "line-through" : "none", color: t.status === "done" ? T.textMuted : T.textPrimary }}>{TYPE_ICON[t.type] ? TYPE_ICON[t.type] + " " : ""}{t.title}</p>
          <p style={{ fontSize: 11, color: overdue ? "#ef4444" : T.textMuted }}>{t.opportunity?.name || t.account?.name || ""}{t.dueDate ? `${t.opportunity || t.account ? " · " : ""}${overdue ? "Overdue " : ""}${crmDate(t.dueDate)}` : ""}</p>
        </div>
        <button onClick={() => del(t)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 14 }}>✕</button>
      </div>
    );
  };
  const section = (label, list, color) => list.length > 0 ? (
    <div>
      <p style={{ fontSize: 11, color: color || T.textMuted, textTransform: "uppercase", letterSpacing: ".06em", margin: "14px 0 2px" }}>{label} <span style={{ color: T.textMuted }}>· {list.length}</span></p>
      {list.map(row)}
    </div>
  ) : null;
  return (
    <div style={card}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} onKeyDown={e => e.key === "Enter" && add()} placeholder="New task…" style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 160 }} />
        <input type="date" value={f.dueDate} onChange={e => setF({ ...f, dueDate: e.target.value })} style={{ ...crmInput, marginBottom: 0, width: 150 }} />
        <button className="btn-secondary" style={{ width: "auto", padding: "0 12px", fontSize: 13 }} onClick={() => setMore(m => !m)} title="More options">⋯</button>
        <button className="btn-primary" style={{ width: "auto", padding: "0 18px", fontSize: 13 }} onClick={add}>Add</button>
      </div>
      {more && (
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <select value={f.type} onChange={e => setF({ ...f, type: e.target.value })} style={{ ...crmInput, marginBottom: 0, width: 130 }}>
            {[["task", "Task"], ["call", "Call"], ["email", "Email"], ["meeting", "Meeting"], ["setup", "Setup"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={f.priority} onChange={e => setF({ ...f, priority: e.target.value })} style={{ ...crmInput, marginBottom: 0, width: 150 }}>
            {CRM_PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label} priority</option>)}
          </select>
          <select value={f.accountId} onChange={e => setF({ ...f, accountId: e.target.value, opportunityId: "" })} style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 140 }}>
            <option value="">— Link account —</option>
            {(accounts || []).map(ac => <option key={ac.id} value={ac.id}>{ac.name}</option>)}
          </select>
          <select value={f.opportunityId} onChange={e => setF({ ...f, opportunityId: e.target.value })} style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 140 }}>
            <option value="">— Link opportunity —</option>
            {(opps || []).filter(o => !f.accountId || o.account?.id === f.accountId).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
      )}
      {tasks === null ? <p style={{ color: T.textMuted, fontSize: 13, marginTop: 14 }}>Loading…</p> : (
        <>
          {open.length === 0 && done.length === 0 && <p style={{ color: T.textMuted, fontSize: 13, marginTop: 14 }}>No tasks yet.</p>}
          {section("Overdue", open.filter(t => bucket(t) === "overdue"), "#ef4444")}
          {section("Today", open.filter(t => bucket(t) === "today"), "#f59e0b")}
          {section("Upcoming", open.filter(t => bucket(t) === "upcoming"))}
          {section("No due date", open.filter(t => bucket(t) === "nodate"))}
          {section("Completed", done)}
        </>
      )}
    </div>
  );
}

function CrmAnalytics({ analytics }) {
  if (!analytics) return <p style={{ color: T.textMuted, fontSize: 13 }}>Loading analytics…</p>;
  const TH = chartTheme();
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20 };
  const pipe = analytics.pipeline || [];
  const fc = analytics.forecastMonths || [];
  const pipelineOption = {
    grid: { top: 16, right: 18, bottom: 30, left: 70 },
    tooltip: { ...TH.tooltip, valueFormatter: v => crmMoney(v) },
    xAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: v => crmMoney(v) }, splitLine: TH.splitLine, axisLine: { show: false } },
    yAxis: { type: "category", data: pipe.map(p => CRM_STAGES.find(s => s.id === p.stage)?.label || p.stage).reverse(), axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    series: [{ type: "bar", barWidth: "60%", data: pipe.slice().reverse().map(p => ({ value: p.value, itemStyle: { color: TH.grad(CRM_STAGES.find(s => s.id === p.stage)?.color || T.accent, (CRM_STAGES.find(s => s.id === p.stage)?.color || T.accent) + "66"), borderRadius: [0, 5, 5, 0] } })), label: { show: true, position: "right", formatter: p => crmMoney(p.value), color: T.textMuted, fontSize: 10 } }],
  };
  const forecastOption = {
    grid: { top: 16, right: 18, bottom: 30, left: 60 },
    tooltip: { ...TH.tooltip, valueFormatter: v => crmMoney(v) },
    xAxis: { type: "category", data: fc.map(f => f.month), axisLabel: TH.axisLabel, axisLine: TH.axisLine, axisTick: { show: false } },
    yAxis: { type: "value", axisLabel: { ...TH.axisLabel, formatter: v => crmMoney(v) }, splitLine: TH.splitLine, axisLine: { show: false } },
    series: [{ type: "bar", barWidth: "50%", data: fc.map(f => f.value), itemStyle: { color: TH.grad("#2ea043", "rgba(46,160,67,0.4)"), borderRadius: [5, 5, 0, 0] }, label: { show: true, position: "top", formatter: p => crmMoney(p.value), color: T.textMuted, fontSize: 10 } }],
  };
  const ob = analytics.onboarding || {};
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 12 }}>Pipeline Value by Stage</p>
          {pipe.some(p => p.value > 0) ? <EChart option={pipelineOption} height={260} /> : <p style={{ color: T.textMuted, fontSize: 12, padding: "40px 0", textAlign: "center" }}>No open pipeline yet</p>}
        </div>
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 12 }}>Weighted Forecast by Close Month</p>
          {fc.length ? <EChart option={forecastOption} height={260} /> : <p style={{ color: T.textMuted, fontSize: 12, padding: "40px 0", textAlign: "center" }}>Set expected close dates to forecast</p>}
        </div>
      </div>
      <div style={card}>
        <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 12 }}>Onboarding Steps</p>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          {[["done", "Done", "#2ea043"], ["in_progress", "In Progress", "#f59e0b"], ["pending", "Pending", "#8b949e"], ["blocked", "Blocked", "#ef4444"]].map(([k, label, c]) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
              <span style={{ fontSize: 13, color: T.textSecondary }}>{label}: <strong style={{ color: T.textPrimary }}>{ob[k] || 0}</strong></span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CrmNewAccount({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", website: "", industry: "", status: "prospect" });
  const [saving, setSaving] = useState(false);
  const save = async () => { if (!f.name.trim()) return; setSaving(true); try { await crmApi("/accounts", { method: "POST", body: JSON.stringify(f) }); onSaved(); } catch (e) { alert(e.message); setSaving(false); } };
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 420, padding: 24 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>New Account</h2>
        <input style={crmInput} placeholder="Company name *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
        <input style={crmInput} placeholder="Website" value={f.website} onChange={e => setF({ ...f, website: e.target.value })} />
        <input style={crmInput} placeholder="Industry" value={f.industry} onChange={e => setF({ ...f, industry: e.target.value })} />
        <select style={crmInput} value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
          <option value="prospect">Prospect</option><option value="active">Active</option><option value="churned">Churned</option>
        </select>
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button className="btn-primary" style={{ flex: 1 }} disabled={saving} onClick={save}>{saving ? "Saving…" : "Create"}</button>
          <button className="btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Leads list (pre-opportunity funnel) ──
const CRM_LEAD_STATUS_META = {
  new:          { label: "New",          color: "#3b82f6" },
  working:      { label: "Working",      color: "#f59e0b" },
  qualified:    { label: "Qualified",    color: "#2ea043" },
  disqualified: { label: "Disqualified", color: "#ef4444" },
  converted:    { label: "Converted",    color: "#8b5cf6" },
};
function CrmLeads({ leads, reload, onConvert, onOpenAcct }) {
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 };
  const setStatus = async (lead, status) => { await crmApi(`/leads/${lead.id}`, { method: "PATCH", body: JSON.stringify({ status }) }); reload(); };
  const del = async (lead) => { if (!window.confirm(`Delete lead “${lead.name}”?`)) return; await crmApi(`/leads/${lead.id}`, { method: "DELETE" }); reload(); };
  const ratingDot = (r) => r === "hot" ? "#ef4444" : r === "warm" ? "#f59e0b" : r === "cold" ? "#3b82f6" : T.textMuted;
  return (
    <div style={card}>
      {leads === null ? <p style={{ color: T.textMuted, fontSize: 13 }}>Loading…</p> :
        leads.length === 0 ? <p style={{ color: T.textMuted, fontSize: 13 }}>No open leads. Capture one with “+ Lead”, then qualify it into an account.</p> : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={{ color: T.textMuted, textAlign: "left" }}>
            {["Lead", "Company", "Source", "Score", "Status", ""].map(h => <th key={h} style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, textTransform: "uppercase" }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {leads.map(l => {
              const m = CRM_LEAD_STATUS_META[l.status] || CRM_LEAD_STATUS_META.new;
              return (
                <tr key={l.id} style={{ borderTop: `1px solid ${T.border}` }}>
                  <td style={{ padding: "10px 12px", fontWeight: 600 }}>
                    <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: ratingDot(l.rating), marginRight: 8 }} title={l.rating || ""} />
                    {l.name}{l.title ? <span style={{ color: T.textMuted, fontWeight: 400 }}> · {l.title}</span> : ""}
                    {l.email ? <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 400, marginLeft: 15 }}>{l.email}</div> : ""}
                  </td>
                  <td style={{ padding: "10px 12px", color: T.textSecondary }}>{l.company || "—"}</td>
                  <td style={{ padding: "10px 12px", color: T.textMuted }}>{l.source || "—"}</td>
                  <td style={{ padding: "10px 12px", color: T.textSecondary, fontFamily: "'JetBrains Mono',monospace" }}>{l.score ?? 0}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <select value={l.status} onChange={e => setStatus(l, e.target.value)}
                      style={{ background: "transparent", border: `1px solid ${T.border}`, borderRadius: 12, padding: "2px 8px", fontSize: 11, color: m.color, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                      {["new", "working", "qualified", "disqualified"].map(s => <option key={s} value={s} style={{ color: T.textPrimary }}>{CRM_LEAD_STATUS_META[s].label}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="btn-primary" style={{ width: "auto", padding: "5px 12px", fontSize: 11 }} onClick={() => onConvert(l)}>Convert →</button>
                    <button onClick={() => del(l)} title="Delete" style={{ marginLeft: 8, background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 14 }}>✕</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CrmNewLead({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", title: "", source: "inbound", rating: "warm", score: 0, notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => { if (!f.name.trim()) return; setSaving(true); try { await crmApi("/leads", { method: "POST", body: JSON.stringify(f) }); onSaved(); } catch (e) { alert(e.message); setSaving(false); } };
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 440, padding: 24 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>New Lead</h2>
        <input style={crmInput} placeholder="Contact name *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
        <div style={{ display: "flex", gap: 10 }}>
          <input style={{ ...crmInput, flex: 1 }} placeholder="Company" value={f.company} onChange={e => setF({ ...f, company: e.target.value })} />
          <input style={{ ...crmInput, flex: 1 }} placeholder="Job title" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <input style={{ ...crmInput, flex: 1 }} placeholder="Email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} />
          <input style={{ ...crmInput, flex: 1 }} placeholder="Phone" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <select style={{ ...crmInput, flex: 1 }} value={f.source} onChange={e => setF({ ...f, source: e.target.value })}>
            {["referral", "inbound", "outbound", "event", "web"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select style={{ ...crmInput, flex: 1 }} value={f.rating} onChange={e => setF({ ...f, rating: e.target.value })}>
            <option value="hot">Hot</option><option value="warm">Warm</option><option value="cold">Cold</option>
          </select>
          <input style={{ ...crmInput, width: 90 }} type="number" placeholder="Score" value={f.score} onChange={e => setF({ ...f, score: e.target.value })} />
        </div>
        <textarea style={{ ...crmInput, minHeight: 60, resize: "vertical" }} placeholder="Notes" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} />
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button className="btn-primary" style={{ flex: 1 }} disabled={saving} onClick={save}>{saving ? "Saving…" : "Create lead"}</button>
          <button className="btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Qualify a lead → Account + Contact + (optional) Opportunity ──
function CrmConvertLead({ lead, accounts, onClose, onDone }) {
  const [f, setF] = useState({
    existingAccountId: "", createOpportunity: true,
    opportunityName: `${lead.company || lead.name} opportunity`, amount: "", expectedCloseDate: "",
  });
  const [saving, setSaving] = useState(false);
  const run = async () => {
    setSaving(true);
    try {
      const body = {
        createOpportunity: f.createOpportunity,
        existingAccountId: f.existingAccountId || undefined,
        opportunityName: f.opportunityName,
        amount: f.amount ? Number(f.amount) : undefined,
        expectedCloseDate: f.expectedCloseDate || undefined,
      };
      const r = await crmApi(`/leads/${lead.id}/convert`, { method: "POST", body: JSON.stringify(body) });
      onDone(r);
    } catch (e) { alert(e.message); setSaving(false); }
  };
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 460, padding: 24 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Qualify lead</h2>
        <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>Convert <b>{lead.name}</b>{lead.company ? ` (${lead.company})` : ""} into CRM records.</p>
        <label style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".05em" }}>Account</label>
        <select style={crmInput} value={f.existingAccountId} onChange={e => setF({ ...f, existingAccountId: e.target.value })}>
          <option value="">➕ Create new account “{(lead.company || lead.name)}”</option>
          {(accounts || []).map(ac => <option key={ac.id} value={ac.id}>Link to existing · {ac.name}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, margin: "8px 0 10px", cursor: "pointer" }}>
          <input type="checkbox" checked={f.createOpportunity} onChange={e => setF({ ...f, createOpportunity: e.target.checked })} />
          Also create an opportunity
        </label>
        {f.createOpportunity && (
          <>
            <input style={crmInput} placeholder="Opportunity name" value={f.opportunityName} onChange={e => setF({ ...f, opportunityName: e.target.value })} />
            <div style={{ display: "flex", gap: 10 }}>
              <input style={{ ...crmInput, flex: 1 }} type="number" placeholder="Amount ($)" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} />
              <input style={{ ...crmInput, flex: 1 }} type="date" value={f.expectedCloseDate} onChange={e => setF({ ...f, expectedCloseDate: e.target.value })} />
            </div>
          </>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button className="btn-primary" style={{ flex: 1 }} disabled={saving} onClick={run}>{saving ? "Converting…" : "Convert"}</button>
          <button className="btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function CrmNewOpp({ accounts, onClose, onSaved, onNeedAccount }) {
  const [f, setF] = useState({ accountId: "", name: "", amount: "", probability: 10, stage: "lead", expectedCloseDate: "", source: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => { if (!f.name.trim() || !f.accountId) return; setSaving(true); try { await crmApi("/opportunities", { method: "POST", body: JSON.stringify(f) }); onSaved(); } catch (e) { alert(e.message); setSaving(false); } };
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 440, padding: 24 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>New Opportunity</h2>
        {(!accounts || accounts.length === 0) ? (
          <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 12 }}>You need an account first. <button onClick={onNeedAccount} style={{ background: "none", border: "none", color: T.accent, cursor: "pointer", textDecoration: "underline" }}>Create one</button></div>
        ) : (
          <select style={crmInput} value={f.accountId} onChange={e => setF({ ...f, accountId: e.target.value })}>
            <option value="">Select account *</option>
            {accounts.map(ac => <option key={ac.id} value={ac.id}>{ac.name}</option>)}
          </select>
        )}
        <input style={crmInput} placeholder="Opportunity name *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
        <div style={{ display: "flex", gap: 10 }}>
          <input style={{ ...crmInput, flex: 1 }} type="number" placeholder="Amount ($)" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} />
          <input style={{ ...crmInput, width: 110 }} type="number" placeholder="Prob %" value={f.probability} onChange={e => setF({ ...f, probability: e.target.value })} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <select style={{ ...crmInput, flex: 1 }} value={f.stage} onChange={e => setF({ ...f, stage: e.target.value })}>
            {CRM_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <input style={{ ...crmInput, flex: 1 }} type="date" value={f.expectedCloseDate} onChange={e => setF({ ...f, expectedCloseDate: e.target.value })} />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button className="btn-primary" style={{ flex: 1 }} disabled={saving} onClick={save}>{saving ? "Saving…" : "Create"}</button>
          <button className="btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function CrmOppModal({ oppId, onClose }) {
  const [opp, setOpp] = useState(null);
  const [note, setNote] = useState("");
  const [noteType, setNoteType] = useState("note");
  const load = () => crmApi(`/opportunities/${oppId}`).then(d => setOpp(d.opportunity)).catch(() => {});
  useEffect(() => { load(); }, [oppId]); // eslint-disable-line
  const setStage = async (stage) => { await crmApi(`/opportunities/${oppId}`, { method: "PATCH", body: JSON.stringify({ stage }) }); load(); };
  const addNote = async () => { if (!note.trim()) return; await crmApi("/activities", { method: "POST", body: JSON.stringify({ opportunityId: oppId, body: note, type: noteType }) }); setNote(""); load(); };
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 560, maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", padding: 24 }} onClick={e => e.stopPropagation()}>
        {!opp ? <p style={{ color: T.textMuted }}>Loading…</p> : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
              <h2 style={{ fontSize: 19, fontWeight: 700 }}>{opp.name}</h2>
              <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>
            <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>{opp.account?.name} · <span style={{ color: T.accent, fontWeight: 600 }}>{crmMoney(opp.amount)}</span> · {opp.probability}% · close {crmDate(opp.expectedCloseDate)}</p>
            <CrmDealAI oppId={oppId} />
            <CrmEmailComposer opportunityId={oppId} onSent={load} />
            <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Stage</p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
              {CRM_STAGES.map(s => (
                <button key={s.id} onClick={() => setStage(s.id)} style={{ padding: "5px 12px", borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", border: `1px solid ${opp.stage === s.id ? s.color : T.border}`, background: opp.stage === s.id ? s.color + "22" : "transparent", color: opp.stage === s.id ? s.color : T.textSecondary }}>{s.label}</button>
              ))}
            </div>
            <CrmLineItems oppId={oppId} opp={opp} items={opp.lineItems || []} reload={load} />
            <CrmCustomFields entity="opportunity" customFields={opp.customFields} onSave={cf => crmApi(`/opportunities/${oppId}`, { method: "PATCH", body: JSON.stringify({ customFields: cf }) }).then(load)} />
            <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Activity</p>
            <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
              {CRM_ACT_TYPES.map(at => (
                <button key={at.id} onClick={() => setNoteType(at.id)} style={{ padding: "4px 10px", borderRadius: 14, fontSize: 12, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", border: `1px solid ${noteType === at.id ? T.accent : T.border}`, background: noteType === at.id ? T.accent + "22" : "transparent", color: noteType === at.id ? T.accent : T.textSecondary }}>{at.icon} {at.label}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <input style={{ ...crmInput, marginBottom: 0, flex: 1 }} placeholder={`Log a ${noteType}…`} value={note} onChange={e => setNote(e.target.value)} onKeyDown={e => e.key === "Enter" && addNote()} />
              <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={addNote}>Log</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(opp.activities || []).map(ac => { const m = crmActMeta(ac.type); return (
                <div key={ac.id} style={{ display: "flex", gap: 10 }}>
                  <span style={{ fontSize: 13, width: 18, textAlign: "center", flexShrink: 0 }}>{m.icon}</span>
                  <div><p style={{ fontSize: 13, color: T.textPrimary }}>{ac.body}</p><p style={{ fontSize: 10, color: T.textMuted }}>{new Date(ac.createdAt).toLocaleString()}</p></div>
                </div>
              ); })}
              {(opp.activities || []).length === 0 && <p style={{ fontSize: 12, color: T.textMuted }}>No activity yet.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CrmAccountModal({ accountId, onClose }) {
  const [acct, setAcct] = useState(null);
  const [ob, setOb] = useState(null);
  const [note, setNote] = useState("");
  const [noteType, setNoteType] = useState("note");
  const load = () => crmApi(`/accounts/${accountId}`).then(d => setAcct(d.account)).catch(() => {});
  const loadOb = () => crmApi(`/onboarding?accountId=${accountId}`).then(setOb).catch(() => {});
  useEffect(() => { load(); loadOb(); }, [accountId]); // eslint-disable-line
  const setStep = async (stepId, status) => { await crmApi("/onboarding", { method: "PATCH", body: JSON.stringify({ stepId, status }) }); loadOb(); };
  const setStatus = async (status) => { await crmApi(`/accounts/${accountId}`, { method: "PATCH", body: JSON.stringify({ status }) }); load(); };
  const addNote = async () => { if (!note.trim()) return; await crmApi("/activities", { method: "POST", body: JSON.stringify({ accountId, body: note, type: noteType }) }); setNote(""); load(); };
  const OB_NEXT = { pending: "in_progress", in_progress: "done", done: "pending" };
  const OB_COLOR = { done: "#2ea043", in_progress: "#f59e0b", pending: "#8b949e", blocked: "#ef4444" };
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 600, maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", padding: 24 }} onClick={e => e.stopPropagation()}>
        {!acct ? <p style={{ color: T.textMuted }}>Loading…</p> : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
                <h2 style={{ fontSize: 19, fontWeight: 700 }}>{acct.name}</h2>
                <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 6 }}>{acct.industry || ""}{acct.website ? ` · ${acct.website}` : ""}</p>
                <select value={acct.status} onChange={e => setStatus(e.target.value)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 12, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", border: `1px solid ${T.border}`, background: acct.status === "active" ? "rgba(46,160,67,.15)" : acct.status === "churned" ? "rgba(239,68,68,.15)" : "rgba(148,163,184,.15)", color: acct.status === "active" ? "#2ea043" : acct.status === "churned" ? "#ef4444" : T.textMuted }}>
                  <option value="prospect">prospect</option><option value="active">active</option><option value="churned">churned</option>
                </select>
              </div>
              <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>

            {/* Onboarding flow */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: T.textSecondary }}>ONBOARDING FLOW</p>
                {ob && <span style={{ fontSize: 12, color: T.accent, fontWeight: 600 }}>{ob.progress}% complete</span>}
              </div>
              {ob && <div style={{ height: 6, background: T.bg, borderRadius: 3, overflow: "hidden", marginBottom: 12 }}><div style={{ height: "100%", width: `${ob.progress}%`, background: "linear-gradient(90deg,#2ea043,#3fb950)" }} /></div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(ob?.steps || []).map(s => (
                  <div key={s.id} onClick={() => setStep(s.id, OB_NEXT[s.status] || "done")} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, background: T.bg, border: `1px solid ${T.border}`, cursor: "pointer" }}>
                    <span style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${OB_COLOR[s.status]}`, background: s.status === "done" ? OB_COLOR[s.status] : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", flexShrink: 0 }}>{s.status === "done" ? "✓" : ""}</span>
                    <span style={{ fontSize: 13, flex: 1, color: s.status === "done" ? T.textMuted : T.textPrimary, textDecoration: s.status === "done" ? "line-through" : "none" }}>{s.label}</span>
                    <span style={{ fontSize: 10, color: OB_COLOR[s.status], textTransform: "uppercase", letterSpacing: ".04em" }}>{s.status.replace("_", " ")}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Opportunities */}
            <p style={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, marginBottom: 8 }}>OPPORTUNITIES</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
              {(acct.opportunities || []).map(o => { const st = CRM_STAGES.find(s => s.id === o.stage); return (
                <div key={o.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", borderRadius: 8, background: T.bg, border: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 13 }}>{o.name}</span>
                  <span style={{ fontSize: 12 }}><span style={{ color: st?.color }}>{st?.label}</span> · <span style={{ color: T.accent, fontWeight: 600 }}>{crmMoney(o.amount)}</span></span>
                </div>
              ); })}
              {(acct.opportunities || []).length === 0 && <p style={{ fontSize: 12, color: T.textMuted }}>No opportunities.</p>}
            </div>

            {/* Contacts */}
            <p style={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, marginBottom: 8 }}>CONTACTS</p>
            <CrmContacts accountId={accountId} contacts={acct.contacts} reload={load} />

            <div style={{ marginTop: 20 }}>
              <CrmCustomFields entity="account" customFields={acct.customFields} onSave={cf => crmApi(`/accounts/${accountId}`, { method: "PATCH", body: JSON.stringify({ customFields: cf }) }).then(load)} />
            </div>

            {/* Activity */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, margin: "20px 0 8px" }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: T.textSecondary }}>ACTIVITY</p>
              <CrmAccountSummary accountId={accountId} />
            </div>
            <CrmEmailComposer accountId={accountId} onSent={load} />
            <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
              {CRM_ACT_TYPES.map(at => (
                <button key={at.id} onClick={() => setNoteType(at.id)} style={{ padding: "4px 10px", borderRadius: 14, fontSize: 12, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", border: `1px solid ${noteType === at.id ? T.accent : T.border}`, background: noteType === at.id ? T.accent + "22" : "transparent", color: noteType === at.id ? T.accent : T.textSecondary }}>{at.icon} {at.label}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <input style={{ ...crmInput, marginBottom: 0, flex: 1 }} placeholder={`Log a ${noteType}…`} value={note} onChange={e => setNote(e.target.value)} onKeyDown={e => e.key === "Enter" && addNote()} />
              <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={addNote}>Log</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(acct.activities || []).map(ac => { const m = crmActMeta(ac.type); return (
                <div key={ac.id} style={{ display: "flex", gap: 10 }}>
                  <span style={{ fontSize: 13, width: 18, textAlign: "center", flexShrink: 0 }}>{m.icon}</span>
                  <div><p style={{ fontSize: 13, color: T.textPrimary }}>{ac.body}</p><p style={{ fontSize: 10, color: T.textMuted }}>{new Date(ac.createdAt).toLocaleString()}</p></div>
                </div>
              ); })}
              {(acct.activities || []).length === 0 && <p style={{ fontSize: 12, color: T.textMuted }}>No activity yet.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── AI deal-risk scoring + next-best-action (Opus) ──
const CRM_RAG = { green: { c: "#2ea043", l: "Healthy" }, amber: { c: "#f59e0b", l: "Needs attention" }, red: { c: "#ef4444", l: "At risk" } };
function CrmDealAI({ oppId }) {
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const run = async () => {
    setLoading(true); setErr(null);
    try { setRes(await crmApi("/ai", { method: "POST", body: JSON.stringify({ action: "deal-risk", opportunityId: oppId }) })); }
    catch (e) { setErr(e.message); } finally { setLoading(false); }
  };
  const rag = res && (CRM_RAG[res.rating] || CRM_RAG.amber);
  return (
    <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>✨ AI Deal Health</span>
        <button className="btn-secondary" style={{ width: "auto", padding: "5px 12px", fontSize: 12 }} disabled={loading} onClick={run}>{loading ? "Analyzing…" : res ? "Re-analyze" : "Analyze risk"}</button>
      </div>
      {err && <p style={{ fontSize: 12, color: "#f87171", marginTop: 8 }}>⚠ {err}</p>}
      {rag && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: rag.c }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: rag.c }}>{rag.l}</span>
            <span style={{ fontSize: 13, color: T.textPrimary }}>— {res.headline}</span>
          </div>
          {(res.risks || []).length > 0 && (
            <ul style={{ margin: "6px 0 8px 18px", padding: 0 }}>
              {res.risks.map((r, i) => <li key={i} style={{ fontSize: 12, color: T.textSecondary, marginBottom: 2 }}>{r}</li>)}
            </ul>
          )}
          <div style={{ fontSize: 12, color: T.textPrimary, background: T.accent + "14", border: `1px solid ${T.accent}44`, borderRadius: 8, padding: "8px 10px" }}>
            <b style={{ color: T.accent }}>Next best action:</b> {res.nextBestAction}
          </div>
        </div>
      )}
    </div>
  );
}

// ── AI account summary (Opus) ──
function CrmAccountSummary({ accountId }) {
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setLoading(true);
    try { setRes(await crmApi("/ai", { method: "POST", body: JSON.stringify({ action: "summarize", accountId }) })); }
    catch (e) { setRes({ summary: "⚠ " + e.message, highlights: [] }); } finally { setLoading(false); }
  };
  return (
    <>
      <button className="btn-secondary" style={{ width: "auto", padding: "4px 12px", fontSize: 11 }} disabled={loading} onClick={run}>{loading ? "Summarizing…" : "✨ Summarize"}</button>
      {res && (
        <div style={{ width: "100%", marginTop: 8, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12 }}>
          <p style={{ fontSize: 13, color: T.textPrimary }}>{res.summary}</p>
          {(res.highlights || []).length > 0 && (
            <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
              {res.highlights.map((h, i) => <li key={i} style={{ fontSize: 12, color: T.textSecondary, marginBottom: 2 }}>{h}</li>)}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

// ── Email composer: AI draft + send via Graph, logged to the timeline ──
function CrmEmailComposer({ opportunityId, accountId, onSent }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ to: "", subject: "", body: "" });
  const [intent, setIntent] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);
  const draft = async () => {
    setDrafting(true); setMsg(null);
    try {
      const d = await crmApi("/ai", { method: "POST", body: JSON.stringify({ action: "draft-email", opportunityId, accountId, intent }) });
      setF(prev => ({ ...prev, subject: d.subject, body: d.body }));
    } catch (e) { setMsg("⚠ " + e.message); } finally { setDrafting(false); }
  };
  const send = async () => {
    if (!f.to.trim() || !f.subject.trim() || !f.body.trim()) { setMsg("Fill in to, subject and body."); return; }
    setSending(true); setMsg(null);
    try {
      await crmApi("/email", { method: "POST", body: JSON.stringify({ ...f, opportunityId, accountId }) });
      setOpen(false); setF({ to: "", subject: "", body: "" }); setIntent("");
      onSent && onSent();
    } catch (e) { setMsg("⚠ " + e.message); } finally { setSending(false); }
  };
  if (!open) return (
    <button className="btn-secondary" style={{ width: "auto", padding: "6px 14px", fontSize: 12, marginBottom: 14 }} onClick={() => setOpen(true)}>✉ Email</button>
  );
  return (
    <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>Email (sent from your mailbox · logged to timeline)</span>
        <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 14 }}>✕</button>
      </div>
      <input style={crmInput} placeholder="To (email address)" value={f.to} onChange={e => setF({ ...f, to: e.target.value })} />
      <div style={{ display: "flex", gap: 8 }}>
        <input style={{ ...crmInput, flex: 1 }} placeholder="What's the goal? (e.g. nudge for a decision)" value={intent} onChange={e => setIntent(e.target.value)} />
        <button className="btn-secondary" style={{ width: "auto", padding: "0 14px", fontSize: 12, marginBottom: 10 }} disabled={drafting} onClick={draft}>{drafting ? "Drafting…" : "✨ Draft"}</button>
      </div>
      <input style={crmInput} placeholder="Subject" value={f.subject} onChange={e => setF({ ...f, subject: e.target.value })} />
      <textarea style={{ ...crmInput, minHeight: 140, resize: "vertical" }} placeholder="Body" value={f.body} onChange={e => setF({ ...f, body: e.target.value })} />
      {msg && <p style={{ fontSize: 12, color: "#f87171", marginBottom: 8 }}>{msg}</p>}
      <button className="btn-primary" style={{ width: "auto", padding: "8px 18px", fontSize: 13 }} disabled={sending} onClick={send}>{sending ? "Sending…" : "Send email"}</button>
    </div>
  );
}

// ── Line items + quote (Phase 3) ──
function CrmLineItems({ oppId, opp, items, reload }) {
  const [products, setProducts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ productId: "", name: "", quantity: 1, unitPrice: "" });
  useEffect(() => { crmApi("/products?active=1").then(d => setProducts(d.products)).catch(() => {}); }, []);
  const total = (items || []).reduce((s, i) => s + i.quantity * i.unitPrice, 0);
  const pickProduct = (id) => {
    const p = products.find(x => x.id === id);
    setF({ productId: id, name: p ? p.name : "", quantity: 1, unitPrice: p ? p.unitPrice : "" });
  };
  const add = async () => {
    if (!f.productId && !f.name.trim()) return;
    await crmApi("/line-items", { method: "POST", body: JSON.stringify({ opportunityId: oppId, ...f, unitPrice: f.unitPrice === "" ? undefined : f.unitPrice }) });
    setF({ productId: "", name: "", quantity: 1, unitPrice: "" }); setAdding(false); reload();
  };
  const patch = async (id, field, val) => { await crmApi("/line-items", { method: "PATCH", body: JSON.stringify({ id, [field]: val }) }); reload(); };
  const del = async (id) => { await crmApi(`/line-items?id=${id}`, { method: "DELETE" }); reload(); };
  const printQuote = () => {
    const rows = (items || []).map(i => `<tr><td>${i.name}</td><td style="text-align:center">${i.quantity}</td><td style="text-align:right">${crmMoney(i.unitPrice)}</td><td style="text-align:right">${crmMoney(i.quantity * i.unitPrice)}</td></tr>`).join("");
    const html = `<html><head><title>Quote — ${opp.name}</title><style>body{font-family:Arial,sans-serif;color:#1a1a1a;padding:40px;max-width:760px;margin:auto}h1{font-size:20px}h2{font-size:15px;color:#c47a10}table{width:100%;border-collapse:collapse;margin-top:16px}th,td{padding:8px 10px;border-bottom:1px solid #ddd;font-size:13px}th{text-align:left;color:#666;font-size:11px;text-transform:uppercase}tfoot td{font-weight:700;border-top:2px solid #333;border-bottom:none}.muted{color:#888;font-size:12px}</style></head><body><h2>PowerVision BI</h2><h1>Quote: ${opp.name}</h1><p class="muted">${opp.account?.name || ""} · ${new Date().toLocaleDateString()}</p><table><thead><tr><th>Item</th><th style="text-align:center">Qty</th><th style="text-align:right">Unit</th><th style="text-align:right">Total</th></tr></thead><tbody>${rows}</tbody><tfoot><tr><td colspan="3" style="text-align:right">Total</td><td style="text-align:right">${crmMoney(total)}</td></tr></tfoot></table></body></html>`;
    const w = window.open("", "_blank"); if (!w) return;
    w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 250);
  };
  return (
    <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>Products / Line Items</span>
        <div style={{ display: "flex", gap: 6 }}>
          {(items || []).length > 0 && <button className="btn-secondary" style={{ width: "auto", padding: "4px 10px", fontSize: 11 }} onClick={printQuote}>🖨 Quote</button>}
          <button className="btn-secondary" style={{ width: "auto", padding: "4px 10px", fontSize: 11 }} onClick={() => setAdding(a => !a)}>{adding ? "Cancel" : "+ Line"}</button>
        </div>
      </div>
      {(items || []).map(i => (
        <div key={i.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ flex: 1, fontSize: 13 }}>{i.name}</span>
          <input type="number" defaultValue={i.quantity} onBlur={e => Number(e.target.value) !== i.quantity && patch(i.id, "quantity", e.target.value)} style={{ ...crmInput, marginBottom: 0, width: 56, padding: "5px 8px", textAlign: "center" }} />
          <span style={{ color: T.textMuted, fontSize: 12 }}>×</span>
          <input type="number" defaultValue={i.unitPrice} onBlur={e => Number(e.target.value) !== i.unitPrice && patch(i.id, "unitPrice", e.target.value)} style={{ ...crmInput, marginBottom: 0, width: 90, padding: "5px 8px", textAlign: "right" }} />
          <span style={{ width: 84, textAlign: "right", fontSize: 13, color: T.accent, fontWeight: 600 }}>{crmMoney(i.quantity * i.unitPrice)}</span>
          <button onClick={() => del(i.id)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 13 }}>✕</button>
        </div>
      ))}
      {(items || []).length === 0 && !adding && <p style={{ fontSize: 12, color: T.textMuted }}>No line items. The deal amount can be itemised here.</p>}
      {adding && (
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
          <select style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 140 }} value={f.productId} onChange={e => pickProduct(e.target.value)}>
            <option value="">— custom line —</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name} ({crmMoney(p.unitPrice)})</option>)}
          </select>
          {!f.productId && <input style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 120 }} placeholder="Item name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />}
          <input type="number" style={{ ...crmInput, marginBottom: 0, width: 56 }} placeholder="Qty" value={f.quantity} onChange={e => setF({ ...f, quantity: e.target.value })} />
          <input type="number" style={{ ...crmInput, marginBottom: 0, width: 90 }} placeholder="Unit $" value={f.unitPrice} onChange={e => setF({ ...f, unitPrice: e.target.value })} />
          <button className="btn-primary" style={{ width: "auto", padding: "7px 14px", fontSize: 12 }} onClick={add}>Add</button>
        </div>
      )}
      {(items || []).length > 0 && <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}`, fontSize: 13 }}>Total: <span style={{ color: T.accent, fontWeight: 700, marginLeft: 8 }}>{crmMoney(total)}</span></div>}
    </div>
  );
}

// ── Per-tenant custom fields renderer (Phase 4) ──
function CrmCustomFields({ entity, customFields, onSave }) {
  const [defs, setDefs] = useState(null);
  const [vals, setVals] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { crmApi(`/fields?entity=${entity}`).then(d => setDefs(d.fields)).catch(() => setDefs([])); }, [entity]);
  useEffect(() => { setVals(customFields || {}); setDirty(false); }, [customFields]);
  if (!defs || defs.length === 0) return null;
  const set = (k, v) => { setVals(prev => ({ ...prev, [k]: v })); setDirty(true); };
  const save = async () => { setSaving(true); try { await onSave(vals); setDirty(false); } finally { setSaving(false); } };
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <p style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>Details</p>
        {dirty && <button className="btn-primary" style={{ width: "auto", padding: "4px 12px", fontSize: 11 }} disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
        {defs.map(fd => (
          <div key={fd.id}>
            <label style={{ fontSize: 11, color: T.textSecondary, display: "block", marginBottom: 3 }}>{fd.label}</label>
            {fd.type === "select" ? (
              <select style={{ ...crmInput, marginBottom: 0 }} value={vals[fd.key] || ""} onChange={e => set(fd.key, e.target.value)}>
                <option value="">—</option>
                {(fd.options || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : fd.type === "checkbox" ? (
              <input type="checkbox" checked={!!vals[fd.key]} onChange={e => set(fd.key, e.target.checked)} />
            ) : (
              <input type={fd.type === "number" ? "number" : fd.type === "date" ? "date" : "text"} style={{ ...crmInput, marginBottom: 0 }} value={vals[fd.key] || ""} onChange={e => set(fd.key, e.target.value)} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── CRM Settings: products, custom fields, automation (Phase 3/4) ──
function CrmSettings({ onClose }) {
  const [tab, setTab] = useState("products");
  return (
    <div style={crmModalWrap} onClick={onClose}>
      <div className="card" style={{ width: 720, maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", padding: 24 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700 }}>CRM Settings</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ display: "flex", gap: 4, marginBottom: 18, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 4, width: "fit-content" }}>
          {[["products", "Products"], ["fields", "Custom Fields"], ["rules", "Automation"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding: "6px 16px", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", background: tab === id ? T.surface : "transparent", color: tab === id ? T.accent : T.textSecondary }}>{label}</button>
          ))}
        </div>
        {tab === "products" && <CrmProducts />}
        {tab === "fields" && <CrmFieldDefs />}
        {tab === "rules" && <CrmRules />}
      </div>
    </div>
  );
}

function CrmProducts() {
  const [list, setList] = useState(null);
  const [f, setF] = useState({ name: "", sku: "", unitPrice: "" });
  const load = () => crmApi("/products").then(d => setList(d.products)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!f.name.trim()) return; await crmApi("/products", { method: "POST", body: JSON.stringify(f) }); setF({ name: "", sku: "", unitPrice: "" }); load(); };
  const toggle = async (p) => { await crmApi(`/products/${p.id}`, { method: "PATCH", body: JSON.stringify({ active: !p.active }) }); load(); };
  const del = async (p) => { if (!window.confirm(`Delete ${p.name}?`)) return; await crmApi(`/products/${p.id}`, { method: "DELETE" }); load(); };
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <input style={{ ...crmInput, marginBottom: 0, flex: 2 }} placeholder="Product name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
        <input style={{ ...crmInput, marginBottom: 0, flex: 1 }} placeholder="SKU" value={f.sku} onChange={e => setF({ ...f, sku: e.target.value })} />
        <input type="number" style={{ ...crmInput, marginBottom: 0, width: 110 }} placeholder="Unit $" value={f.unitPrice} onChange={e => setF({ ...f, unitPrice: e.target.value })} />
        <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={add}>Add</button>
      </div>
      {list === null ? <p style={{ color: T.textMuted, fontSize: 13 }}>Loading…</p> : list.length === 0 ? <p style={{ color: T.textMuted, fontSize: 13 }}>No products yet.</p> : list.map(p => (
        <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderTop: `1px solid ${T.border}` }}>
          <span style={{ flex: 1, fontSize: 13, opacity: p.active ? 1 : 0.5 }}>{p.name}{p.sku ? <span style={{ color: T.textMuted }}> · {p.sku}</span> : ""}</span>
          <span style={{ fontSize: 13, color: T.accent, fontWeight: 600 }}>{crmMoney(p.unitPrice)}</span>
          <button className="btn-secondary" style={{ width: "auto", padding: "3px 10px", fontSize: 11 }} onClick={() => toggle(p)}>{p.active ? "Active" : "Inactive"}</button>
          <button onClick={() => del(p)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer" }}>✕</button>
        </div>
      ))}
    </div>
  );
}

function CrmFieldDefs() {
  const [list, setList] = useState(null);
  const [f, setF] = useState({ entity: "account", label: "", type: "text", options: "" });
  const load = () => crmApi("/fields").then(d => setList(d.fields)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.label.trim()) return;
    const body = { entity: f.entity, label: f.label, type: f.type, options: f.type === "select" ? f.options.split(",").map(s => s.trim()).filter(Boolean) : undefined };
    try { await crmApi("/fields", { method: "POST", body: JSON.stringify(body) }); setF({ ...f, label: "", options: "" }); load(); } catch (e) { alert(e.message); }
  };
  const del = async (fd) => { await crmApi(`/fields?id=${fd.id}`, { method: "DELETE" }); load(); };
  return (
    <div>
      <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 10 }}>Custom fields appear in the Details section of each record.</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <select style={{ ...crmInput, marginBottom: 0, width: 130 }} value={f.entity} onChange={e => setF({ ...f, entity: e.target.value })}>
          <option value="account">Account</option><option value="opportunity">Opportunity</option>
        </select>
        <input style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 140 }} placeholder="Field label" value={f.label} onChange={e => setF({ ...f, label: e.target.value })} />
        <select style={{ ...crmInput, marginBottom: 0, width: 120 }} value={f.type} onChange={e => setF({ ...f, type: e.target.value })}>
          {["text", "number", "select", "date", "checkbox"].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={add}>Add</button>
      </div>
      {f.type === "select" && <input style={{ ...crmInput }} placeholder="Options, comma-separated (e.g. SMB, Mid, Enterprise)" value={f.options} onChange={e => setF({ ...f, options: e.target.value })} />}
      {(list || []).map(fd => (
        <div key={fd.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderTop: `1px solid ${T.border}` }}>
          <span style={{ fontSize: 11, color: T.textMuted, width: 90, textTransform: "capitalize" }}>{fd.entity}</span>
          <span style={{ flex: 1, fontSize: 13 }}>{fd.label}</span>
          <span style={{ fontSize: 11, color: T.textMuted }}>{fd.type}</span>
          <button onClick={() => del(fd)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer" }}>✕</button>
        </div>
      ))}
    </div>
  );
}

function CrmRules() {
  const [list, setList] = useState(null);
  const [f, setF] = useState({ name: "", toStage: "", taskTitle: "", taskType: "task", priority: "normal", dueInDays: 3 });
  const load = () => crmApi("/rules").then(d => setList(d.rules)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.name.trim() || !f.taskTitle.trim()) return;
    const body = { name: f.name, config: { toStage: f.toStage || null, taskTitle: f.taskTitle, taskType: f.taskType, priority: f.priority, dueInDays: f.dueInDays === "" ? null : Number(f.dueInDays) } };
    try { await crmApi("/rules", { method: "POST", body: JSON.stringify(body) }); setF({ ...f, name: "", taskTitle: "" }); load(); } catch (e) { alert(e.message); }
  };
  const toggle = async (r) => { await crmApi("/rules", { method: "PATCH", body: JSON.stringify({ id: r.id, active: !r.active }) }); load(); };
  const del = async (r) => { await crmApi(`/rules?id=${r.id}`, { method: "DELETE" }); load(); };
  return (
    <div>
      <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 10 }}>When an opportunity moves into a stage, automatically create a follow-up task. Use <code>{"{opp}"}</code> for the deal name.</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <input style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 140 }} placeholder="Rule name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
        <select style={{ ...crmInput, marginBottom: 0, width: 130 }} value={f.toStage} onChange={e => setF({ ...f, toStage: e.target.value })}>
          <option value="">Any stage</option>
          {CRM_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <input style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 160 }} placeholder="Task title (e.g. Send proposal to {opp})" value={f.taskTitle} onChange={e => setF({ ...f, taskTitle: e.target.value })} />
        <select style={{ ...crmInput, marginBottom: 0, width: 110 }} value={f.priority} onChange={e => setF({ ...f, priority: e.target.value })}>
          {["low", "normal", "high"].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <input type="number" style={{ ...crmInput, marginBottom: 0, width: 100 }} placeholder="Due in days" value={f.dueInDays} onChange={e => setF({ ...f, dueInDays: e.target.value })} />
        <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={add}>Add</button>
      </div>
      {(list || []).map(r => (
        <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderTop: `1px solid ${T.border}` }}>
          <span style={{ flex: 1, fontSize: 13, opacity: r.active ? 1 : 0.5 }}>{r.name} <span style={{ color: T.textMuted, fontSize: 11 }}>→ {r.config?.toStage || "any"}: “{r.config?.taskTitle}”</span></span>
          <button className="btn-secondary" style={{ width: "auto", padding: "3px 10px", fontSize: 11 }} onClick={() => toggle(r)}>{r.active ? "On" : "Off"}</button>
          <button onClick={() => del(r)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer" }}>✕</button>
        </div>
      ))}
    </div>
  );
}

function CrmContacts({ accountId, contacts, reload }) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: "", email: "", role: "" });
  const save = async () => { if (!f.name.trim()) return; await crmApi("/contacts", { method: "POST", body: JSON.stringify({ ...f, accountId }) }); setF({ name: "", email: "", role: "" }); setAdding(false); reload(); };
  const del = async (id) => { await crmApi(`/contacts?id=${id}`, { method: "DELETE" }); reload(); };
  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
        {(contacts || []).map(c => (
          <div key={c.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", borderRadius: 8, background: T.bg, border: `1px solid ${T.border}` }}>
            <div><span style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</span>{c.role ? <span style={{ fontSize: 12, color: T.textMuted }}> · {c.role}</span> : ""}<div style={{ fontSize: 11, color: T.textMuted }}>{c.email}</div></div>
            <button onClick={() => del(c.id)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer" }}>✕</button>
          </div>
        ))}
        {(contacts || []).length === 0 && !adding && <p style={{ fontSize: 12, color: T.textMuted }}>No contacts.</p>}
      </div>
      {adding ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 120 }} placeholder="Name *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
          <input style={{ ...crmInput, marginBottom: 0, flex: 1, minWidth: 120 }} placeholder="Email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} />
          <input style={{ ...crmInput, marginBottom: 0, width: 120 }} placeholder="Role" value={f.role} onChange={e => setF({ ...f, role: e.target.value })} />
          <button className="btn-primary" style={{ width: "auto", padding: "0 16px", fontSize: 13 }} onClick={save}>Add</button>
        </div>
      ) : <button onClick={() => setAdding(true)} style={{ background: "none", border: `1px solid ${T.accent}`, borderRadius: 6, padding: "5px 12px", color: T.accent, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>+ Contact</button>}
    </div>
  );
}

// ─── App ────────────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("login");
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  // On mount, check if user is already signed in (MSAL session)
// On mount, check if user is already signed in (MSAL session)
useEffect(() => {
  async function checkSession() {
    const msal = getMsalInstance();
    if (!msal) return;
    await msal.initialize();
    
    // Handle redirect coming back from Microsoft
    const redirectResult = await msal.handleRedirectPromise();
    if (redirectResult) {
      setUser({
        email: redirectResult.account.username,
        name: redirectResult.account.name || redirectResult.account.username.split("@")[0],
      });
      setToken(redirectResult.idToken);
      sessionStorage.setItem("pvbi_token", redirectResult.idToken);
      setScreen("dashboard");
      return;
    }

    // Check if already signed in
    const accounts = msal.getAllAccounts();
    if (accounts.length) {
      try {
        const result = await msal.acquireTokenSilent({
          scopes: ["openid", "profile", "email"],
          account: accounts[0],
        });
        setUser({ email: accounts[0].username, name: accounts[0].name || accounts[0].username.split("@")[0] });
        setToken(result.idToken);
        sessionStorage.setItem("pvbi_token", result.idToken);
        setScreen("dashboard");
      } catch {
        // Silent token failed, stay on login
      }
    }
  }
  checkSession();
}, []);

 const handleLogin = (u, t) => {
    setUser(u); 
    setToken(t); 
    sessionStorage.setItem("pvbi_token", t);
    setScreen("dashboard");
  };

  const handleLogout = async () => {
    try { await signOut(); } catch {}
    try { window.google?.accounts?.id?.disableAutoSelect?.(); } catch {}
    sessionStorage.removeItem("pvbi_provider");
    setUser(null); setToken(null); setScreen("login");
  };

  return (
    <ErrorBoundary>
      <>
        <style>{css}</style>
        <div className="grid-bg" />
        <div className="orb" style={{ width: 500, height: 500, top: -160, left: -120, background: "rgba(196,122,16,0.07)" }} />
        <div className="orb" style={{ width: 350, height: 350, bottom: -100, right: -80, background: "rgba(196,122,16,0.04)" }} />
        {screen === "login" && <Login onLogin={handleLogin} />}
        {screen === "dashboard" && <Dashboard user={user} token={token} onLogout={handleLogout} />}
      </>
    </ErrorBoundary>
  );
}
